import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    Radio,
    Button,
    Text,
    TooltipTrigger,
    sparkToast,
    Input,
    SparkModalPortal,
    DropdownMenu,
    Spinner,
    SparkIcon,
} from '@spark';
import '@/features/datasets/components/sparkSelectFullWidth.css';

/** Inline replacement for the deleted StepsModal design-system component. */
const StepsModal = ({ visible, onCancel, title, subtitle, steps, currentStep, onStepChange, children, width = 600, styles: customStyles = {} }) => (
    <SparkModalPortal
        open={visible}
        onClose={onCancel}
        destroyOnClose
        size="slot"
        // Rendered above the Spark modal portal (z-index 1100) like the old
        // antd modal (zIndex 2000).
        style={{ zIndex: 2000 }}
    >
        <div
            style={{
                position: 'relative',
                background: '#fff',
                borderRadius: 12,
                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.08)',
                padding: 20,
                maxHeight: 'calc(100vh - 48px)',
                overflowY: 'auto',
                width: `${width + 50}px`,
                maxWidth: `${width + 50}px`,
                margin: '0 auto',
                ...customStyles.content,
            }}
        >
            <Button
                variant="ghost"
                size="small"
                icon={<SparkIcon slug="close" size="small" />}
                aria-label="Close"
                onClick={onCancel}
                style={{ position: 'absolute', top: 12, right: 12 }}
            />
            <div style={{ ...customStyles.body }}>
                <div style={{ padding: 8 }}>
                    <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 2 }}>{title}</div>
                    {subtitle && <div style={{ color: '#6B7280', fontSize: 14, marginBottom: 18, fontWeight: 400 }}>{subtitle}</div>}
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', fontSize: 15, marginBottom: 24, rowGap: 8, columnGap: 0 }}>
                        {steps.map((step, index) => (
                            <React.Fragment key={index}>
                                <span
                                    style={{
                                        color: currentStep === index ? '#1677FF' : '#111827',
                                        fontSize: 15, fontWeight: 300,
                                        cursor: onStepChange ? 'pointer' : 'default',
                                        display: 'inline-flex', alignItems: 'center', gap: 8, lineHeight: 1.4,
                                    }}
                                    onClick={() => onStepChange?.(index)}
                                >
                                    {index < currentStep
                                        ? <span style={{ color: '#1677FF', display: 'inline-flex' }}><SparkIcon slug="check" size="small" /></span>
                                        : <span style={{ color: currentStep === index ? '#1677FF' : '#111827', fontWeight: 300 }}>{index + 1}.</span>}
                                    <span>{step}</span>
                                </span>
                                {index < steps.length - 1 && (
                                    <span style={{ color: '#D1D5DB', margin: '0 14px', display: 'inline-flex' }}>
                                        <SparkIcon slug="chevron-right" size="small" />
                                    </span>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                    {children}
                </div>
            </div>
        </div>
    </SparkModalPortal>
);
import { data_from_server_post } from '@/shared/http/client';
import { TaskType, MAX_LABELS_TO_DISPLAY, getLabelColumnPluralLabel, isGenerativeTask, getGenerativeOutputNoun } from '@/shared/domain/taskTypes';
import LabelExtractionManager from '@/features/datasets/components/LabelExtractionManager';
import RejectionLabelNotice from '@/features/datasets/components/RejectionLabelNotice';
import PromptTemplateStep from '@/features/datasets/components/PromptTemplateStep';
import ExtractedContentDisplay from '@/features/datasets/components/ExtractedContentDisplay';
import {
    findCsvEndIndexForRowCount,
    getCsvFieldValueFromFirstRecord,
} from '@/features/datasets/utils/csvPreviewUtils';
import { fieldValuesFromUploadEntry, getFieldValuesFromFirstNRecords, isJsonLikeFormat, parseFirstJsonlObject } from '@/features/datasets/utils/filePreviewUtils';
import { detectLabelsDelimiter } from '@/features/datasets/utils/labelsDelimiter';
import { formatFieldPreview, getFieldValue } from '@/features/datasets/utils/nestedFieldValue';
import ReviewOutputsStep from '@/features/datasets/components/datasetUpload/steps/ReviewOutputsStep';
import { useUploadModalState } from '@/features/datasets/components/datasetUpload/hooks/useUploadModalState';
import HasLabelsSwitch from '@/features/datasets/components/datasetUpload/HasLabelsSwitch';
import NoLabelsModeChoice from '@/features/datasets/components/datasetUpload/NoLabelsModeChoice';
import EmptyResponsesChoice from '@/features/datasets/components/datasetUpload/EmptyResponsesChoice';
import AiGeneratedLabelsBlock from '@/features/datasets/components/datasetUpload/AiGeneratedLabelsBlock';
import AIGeneratedLabelsManager from '@/features/datasets/components/AIGeneratedLabelsManager';
import { aiLabelRowsToMapping as aiLabelRowsToMappingUtil, makeEmptyAiLabelRow } from '@/features/tasks/utils/aiLabels';
import {
    NO_LABELS_MODE_DEFAULT,
    NO_LABELS_MODE_GENERATE_WITH_AI,
    NO_LABELS_MODE_UNLABELED,
    isGenerativeAiEmptyColumnTask,
} from '@/features/datasets/utils/hasLabelsHandling';
import ConfirmNoOutputsStep from '@/features/datasets/components/datasetUpload/steps/ConfirmNoOutputsStep';
import {
    extractFileContentForColumns,
    fetchFileLabelsWithUpload,
    previewTruncationMessage,
    MAX_PREVIEW_FILE_SIZE_BYTES,
    PREVIEW_TRUNCATION_BYTES,
} from '@/features/datasets/utils/datasetUploadFlow';

const InlineColumnSelector = ({ label, value, onChange, options = [], placeholder = "Select column", loading = false, error, title, tooltipText }) => {
    const [open, setOpen] = useState(false);
    const headingText = title || `Select ${label} column`;
    const tooltip = tooltipText || `Select the column that contains the ${label} for the examples`;
    const normalizedOptions = (options || []).map((opt) => {
        if (typeof opt === 'string') return { value: opt, label: opt };
        const v = opt?.value ?? '';
        return { value: v, label: opt?.label ?? String(v) };
    });
    const selectedOption = normalizedOptions.find((o) => o.value === value);
    const items = normalizedOptions.map((o) => ({
        label: String(o.label),
        state: o.value === value ? 'selected' : 'default',
        onClick: () => {
            onChange(o.value);
            setOpen(false);
        },
    }));
    return (
        <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', gap: '8px' }}>
                <Text variant="base-regular" style={{ fontWeight: 400, color: '#222', fontSize: '14px', margin: 0 }}>{headingText}</Text>
                <TooltipTrigger text={tooltip}>
                    <span style={{ color: '#878787', cursor: 'help', display: 'inline-flex' }}>
                        <SparkIcon slug="circle-help" size="small" />
                    </span>
                </TooltipTrigger>
                {loading ? <Spinner size="small" /> : null}
            </div>
            <DropdownMenu
                selectType="single-select"
                size="medium"
                showLabel={false}
                placeholder={loading ? 'Loading...' : placeholder}
                text={selectedOption ? String(selectedOption.label) : undefined}
                disabled={loading}
                open={open}
                onOpenChange={setOpen}
                items={items}
                className="spark-select-full-width"
            />
            {error ? <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 6 }}>{error}</div> : null}
        </div>
    );
};

// Preview extraction (labels/entities/relations/summaries) can be slow for large files.
// We still allow uploading/processing large files; we just skip previewing values in the
// modal (size limits shared via datasetUploadFlow).

function getPreviewErrorMessage(taskType) {
    return previewTruncationMessage(taskType);
}

// For very large files we do not parse client-side (brittle for huge JSON records).
// Instead we send only the first 20MB to the backend preview endpoint and show a warning.
/**
 * Helper function to find default columns based on task type
 * @param {Array} columns - Array of column names or objects with value/label
 * @param {string} taskType - The task type (classification, NER, NER_WITH_RELATIONS)
 * @returns {Object} Object with labelColumn and optionally relationsColumn
 */
const findDefaultColumns = (columns, taskType) => {
    if (!columns || columns.length === 0) {
        return { labelColumn: null, relationsColumn: null };
    }

    // Convert columns to array of strings (handle both string and object formats)
    const columnNames = columns.map(col => {
        if (typeof col === 'object' && col !== null) {
            return col.value || col.label;
        }
        return col;
    });

    let labelColumn = null;
    let relationsColumn = null;

    if (taskType === TaskType.classification) {
        // For classification: search for "labels" first, then "label"
        if (columnNames.includes('labels')) {
            labelColumn = 'labels';
        } else if (columnNames.includes('label')) {
            labelColumn = 'label';
        }
    } else if (taskType === TaskType.NER) {
        // For NER: search for "entities"
        if (columnNames.includes('entities')) {
            labelColumn = 'entities';
        }
    } else if (taskType === TaskType.NER_WITH_RELATIONS) {
        // For NER_WITH_RELATIONS: search for "entities" and "relations"
        if (columnNames.includes('entities')) {
            labelColumn = 'entities';
        }
        if (columnNames.includes('relations')) {
            relationsColumn = 'relations';
        }
    } else if (taskType === TaskType.summarization) {
        if (columnNames.includes('summary')) {
            labelColumn = 'summary';
        } else if (columnNames.includes('summaries')) {
            labelColumn = 'summaries';
        }
    } else if (taskType === TaskType.contentGeneration) {
        if (columnNames.includes('response')) {
            labelColumn = 'response';
        } else if (columnNames.includes('responses')) {
            labelColumn = 'responses';
        }
    }

    return { labelColumn, relationsColumn };
};

/**
 * @param {Object|null|undefined} options - `get-labels` shape: { label_names, entity_names, relation_names }
 * @param {'Label'|'Entity'|'Relation'|'Summary'} labelType
 * @returns {string[]}
 */
function pickAvailableLabelsFromOptions(options, labelType) {
    if (!options || typeof options !== 'object') return [];
    if (labelType === 'Entity') return options.entity_names || [];
    if (labelType === 'Relation') return options.relation_names || [];
    return options.label_names || [];
}

// Component for Review Extracted Labels section
const ReviewExtractedLabels = ({
    datasetId,
    columns,
    loading,
    selectedLabelColumn,
    selectedTextColumn,
    selectedRelationsColumn,
    onLabelColumnChange,
    onTextColumnChange,
    onRelationsColumnChange,
    labelError,
    taskType,
    isMultilabel,
    delimiter,
    setDelimiter,
    selectedTaskType
}) => {
    // Determine column labels based on task type
    const getColumnLabels = () => {
        if (taskType === TaskType.NER_WITH_RELATIONS) { // NER_WITH_RELATIONS
            return {
                labelColumn: "entities",
                relationsColumn: "relations"
            };
        } else if (taskType === TaskType.NER) { // NER
            return {
                labelColumn: "entities"
            };
        }
        else if (taskType === TaskType.summarization) {
            return {
                labelColumn: "summary"
            };
        } else if (taskType === TaskType.contentGeneration) {
            return {
                labelColumn: "response"
            };
        } else {
            return {
                labelColumn: "labels"
            };
        }
    };

    const columnLabels = getColumnLabels();

    return (
        <div style={{ marginTop: '32px' }}>
            <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
                Review Extracted Labels
            </div>
            <InlineColumnSelector
                label={columnLabels.labelColumn}
                title={`Select ${columnLabels.labelColumn} field`}
                tooltipText={`Select the column that contains the ${columnLabels.labelColumn} for the examples`}
                value={selectedLabelColumn}
                onChange={onLabelColumnChange}
                options={columns}
                placeholder={`Select ${columnLabels.labelColumn} column`}
                loading={loading}
                error={labelError}
            />
            {taskType === TaskType.classification && isMultilabel && selectedLabelColumn && (
                <InlineColumnSelector
                    label="labels"
                    title="Select labels separator (delimiter)"
                    tooltipText="Select the separator used to separate labels in the labels column"
                    value={delimiter}
                    onChange={setDelimiter}
                    options={[
                        { value: ';', label: 'Semicolon ;' },
                        { value: ',', label: 'Comma ,' },
                        { value: ' ', label: 'Space ␣' },
                        { value: '|', label: 'Pipe |' },
                        { value: '\t', label: 'Tab \\t' },
                        { value: 'list', label: 'List []' }
                    ]}
                    placeholder="Delimiter"
                />
            )}
            {taskType === "EntityRecognitionResponse" && (
                <InlineColumnSelector
                    label={columnLabels.relationsColumn}
                    title={`Select ${columnLabels.relationsColumn} field`}
                    tooltipText={`Select the column that contains the ${columnLabels.relationsColumn} for the examples`}
                    value={selectedRelationsColumn}
                    onChange={onRelationsColumnChange}
                    options={columns}
                    placeholder={`Select ${columnLabels.relationsColumn} column`}
                    loading={loading}
                />
            )}
        </div>
    );
};

const DatasetUploadStepsModal = ({
    visible,
    onCancel,
    onConfirm,
    fileName,
    onOpenHelp,
    // Step 1 props (Data Creation)
    selectedOrigin,
    onOriginChange,
    // Step 2 props (Label Review) - for HuggingFace datasets
    datasetId,
    columns,
    columnsLoading,
    selectedLabelColumn,
    selectedTextColumn,
    onLabelColumnChange,
    onTextColumnChange,
    labelError,
    extractedLabels,
    labelsLoading,
    labelMappings,
    onDeleteLabel,
    onReplaceLabel,
    onAddNewLabel,
    // Step 2 props (Label Review) - for uploaded files
    clientId,
    llmUsecaseId,
    allLabelOptions: allLabelOptionsProp = null,
    uploadedFileName,
    uploadedFileContent,
    uploadedFileType,
    uploadedFile,
    taskType,
    isMultilabel,
    delimiter,
    setDelimiter,
    // Single-label classification "Other"/rejection fallback label already
    // saved on the task (from a previous upload / Edit Task). Used to
    // pre-check the matching row so the choice persists across uploads.
    existingRejectionLabelName = null,
    // Whether the task enabled the confidence-threshold fallback. The "Set as
    // 'Other'" column is only relevant (and shown) when this is true.
    confidenceRejectionEnabled = false,
    // Actions
    onSkip,
    onNext,
    onDone,
    uploading = false,
    // Library dataset mode: pre-supply column names and default mappings so we skip file parsing
    preloadedColumns,
    defaultColumnMappings,
    isBenchmark = false,
    // Multi-objective task wiring (opt-in). When `subGroup` is a multi-
    // objective sub-group (multiple usecase rows sharing a sub_group_name),
    // step 3 renders the multi-objective ReviewOutputsStep and the modal
    // calls `onDoneObject` with the new object payload instead of the
    // legacy positional onDone.
    subGroup = null,
    onDoneObject = null,
}) => {
    const multiObjectiveMode =
        !!subGroup && (subGroup.usecases || []).length > 1;
    const multiObjective = useUploadModalState(multiObjectiveMode ? subGroup : null);
    const {
        patchEntry: patchPerOutputEntry,
        getEntry: getPerOutputEntry,
        resetEntry: resetPerOutputEntry,
    } = multiObjective;
    const appliedMultiObjectiveDefaultsKeyRef = useRef(null);
    const multiObjectivePerOutputRef = useRef(multiObjective.perOutput);
    multiObjectivePerOutputRef.current = multiObjective.perOutput;
    const patchMultiObjectiveEntryRef = useRef(async () => { });
    const multiObjectiveLabelsRequestIdRef = useRef(0);
    const [multiObjectiveValidation, setMultiObjectiveValidation] = useState({
        ok: true,
        anyEmpty: false,
        errors: {},
        softErrors: {},
    });
    // Active sub-task tab in the multi-objective "Review Outputs" step. Lifted to
    // the modal so the footer can show "Next" until the user reaches the final
    // sub-task tab and only show "Done" on the last one.
    const [activeUsecaseId, setActiveUsecaseId] = useState(null);
    const isPreviewTruncated = !!(uploadedFile && uploadedFile.size > MAX_PREVIEW_FILE_SIZE_BYTES);
    const previewTruncationNotice = isPreviewTruncated ? getPreviewErrorMessage(taskType) : '';
    const isPreloaded = Array.isArray(preloadedColumns);
    const stepOffset = isPreloaded ? 1 : 0;
    const [currentStep, setCurrentStep] = useState(stepOffset);
    const [originError, setOriginError] = useState('');
    const [step2Error, setStep2Error] = useState('');
    const [step1Error, setStep1Error] = useState('');
    const [showConfirmationStep, setShowConfirmationStep] = useState(false);
    const [fetchedLabelOptions, setFetchedLabelOptions] = useState(null);
    /** @type {[Record<string, { label_names?: string[], entity_names?: string[], relation_names?: string[] }>, function]} */
    const [labelsByUsecaseId, setLabelsByUsecaseId] = useState({});
    const [fileHasLabels, setFileHasLabels] = useState(true);
    const [fileNoLabelsMode, setFileNoLabelsMode] = useState(NO_LABELS_MODE_DEFAULT);
    const [aiGeneratedLabels, setAiGeneratedLabels] = useState([]); // [{id, name, replaceWith}]
    const [internalDelimiter, setInternalDelimiter] = useState(null);
    const effectiveDelimiter = delimiter !== undefined ? delimiter : internalDelimiter;
    const effectiveSetDelimiter = setDelimiter || setInternalDelimiter;

    useEffect(() => {
        if (!visible || !llmUsecaseId || allLabelOptionsProp) return;
        data_from_server_post('get-labels', llmUsecaseId, (response) => {
            setFetchedLabelOptions(response || {});
        });
    }, [visible, llmUsecaseId, allLabelOptionsProp]);

    const allLabelOptions = allLabelOptionsProp || fetchedLabelOptions || {};
    const getAvailableLabelsForType = useCallback(
        (labelType) => pickAvailableLabelsFromOptions(allLabelOptions, labelType),
        [allLabelOptions]
    );

    const multiObjectiveLabelCatalogKey = useMemo(() => {
        if (!multiObjectiveMode || !subGroup?.usecases?.length) return '';
        return [...subGroup.usecases].map((u) => String(u._id)).sort().join(',');
    }, [multiObjectiveMode, subGroup]);

    // Start every open of the modal from the first sub-task tab. The modal stays
    // mounted between opens, so without this reset `activeUsecaseId` would retain
    // the last-viewed tab (e.g. the final sub-task) on the next open. Keyed on the
    // stable usecase-id catalog so navigating between tabs doesn't reset it.
    useEffect(() => {
        if (!visible || !multiObjectiveMode) return;
        const usecases = subGroup?.usecases || [];
        if (usecases.length === 0) return;
        setActiveUsecaseId(String(usecases[0]._id));
        // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only on open / objective-set change
    }, [visible, multiObjectiveMode, multiObjectiveLabelCatalogKey]);

    useEffect(() => {
        if (!visible || !multiObjectiveMode || !multiObjectiveLabelCatalogKey) return undefined;

        setLabelsByUsecaseId({});
        const reqId = ++multiObjectiveLabelsRequestIdRef.current;
        const usecases = subGroup.usecases || [];

        usecases.forEach((uc) => {
            const id = String(uc._id);
            data_from_server_post('get-labels', uc._id, (response) => {
                if (multiObjectiveLabelsRequestIdRef.current !== reqId) return;
                setLabelsByUsecaseId((prev) => ({
                    ...prev,
                    [id]: response || {},
                }));
            });
        });

        return () => {
            multiObjectiveLabelsRequestIdRef.current += 1;
        };
    }, [visible, multiObjectiveMode, multiObjectiveLabelCatalogKey, subGroup]);

    // Reset to first step when modal opens
    useEffect(() => {
        if (visible) {
            setCurrentStep(stepOffset);
            setOriginError('');
            setStep2Error('');
            setStep1Error('');
            setShowConfirmationStep(false);
            setHasAttemptedSubmission(false);
            setFileHasLabels(true);
            setAiGeneratedLabels([]);
            setLabelsByUsecaseId({});

            // Reset all file-related state when modal opens for a new file
            setInternalDelimiter(null);
            delimiterDetectedForRef.current = {};
            setFileColumnsLoading(false);
            setFileSelectedLabelColumn(null);
            setDetectedLabelColumn(null);
            setFileSelectedTextColumn(null);
            setFileSelectedRelationsColumn(null);
            setFileExtractedLabels([]);
            setFileLabelsLoading(false);
            setFileLabelMappings({});
            setFileLabelExamples({});
            setFileLabelError('');
            setFileExtractedRelations([]);
            setFileRelationsLoading(false);
            setFileRelationsMappings({});

            // Reset extracted summary state
            setExtractedSummary(null);
            setSummaryLoading(false);

            // Reset prompt template state
            setExamplesAreWithPrompt(false);
            setPromptTemplate('');
            setSelectedDynamicFields([]);
            setFieldOriginalTextMap({});
            setExampleDynamicField(null);
            exampleDynamicFieldTouchedRef.current = false;
            setContentField(null);
            setExampleField(null);

            if (isPreloaded) {
                setFileColumns(preloadedColumns);
                const preloadedColumnNames = preloadedColumns
                    .map(col => (typeof col === 'object' && col !== null ? (col.value || col.label) : col))
                    .filter(Boolean);
                const exCol =
                    defaultColumnMappings?.example_column_name ||
                    (preloadedColumnNames.includes('example')
                        ? 'example'
                        : preloadedColumnNames.includes('text')
                            ? 'text'
                            : null);
                if (exCol) {
                    setFileSelectedTextColumn(exCol);
                    setContentField(exCol);
                    setExampleField(exCol);
                }
                if (defaultColumnMappings?.label_column_name) {
                    setFileSelectedLabelColumn(defaultColumnMappings.label_column_name);
                    setDetectedLabelColumn(defaultColumnMappings.label_column_name);
                }
                if (defaultColumnMappings?.relations_column_name) {
                    setFileSelectedRelationsColumn(defaultColumnMappings.relations_column_name);
                }
                if (defaultColumnMappings?.classification_labels_delimiter) {
                    effectiveSetDelimiter(defaultColumnMappings.classification_labels_delimiter);
                } else if (isMultilabel && taskType === TaskType.classification) {
                    effectiveSetDelimiter('list');
                }
            } else {
                setFileColumns([]);
            }

            labelRequestIdRef.current += 1;
            relationsRequestIdRef.current += 1;

            if (multiObjectiveMode) {
                (subGroup?.usecases || []).forEach((uc) => {
                    resetPerOutputEntry(uc._id);
                    // Pre-seed each single-label classification subtask's
                    // "Other"/rejection fallback from the value already saved
                    // on the usecase, so the choice persists across uploads.
                    const savedRejection =
                        uc?.task_type === 'ClassificationResponse' &&
                        uc?.is_multilabel !== true
                            ? (uc?.rejection_label_name || null)
                            : null;
                    if (savedRejection) {
                        patchPerOutputEntry(uc._id, {
                            rejectionLabelName: savedRejection,
                        });
                    }
                });
                appliedMultiObjectiveDefaultsKeyRef.current = null;
            }
        } else {
            appliedMultiObjectiveDefaultsKeyRef.current = null;
            setLabelsByUsecaseId({});
        }
    }, [
        visible,
        stepOffset,
        isPreloaded,
        preloadedColumns,
        defaultColumnMappings,
        multiObjectiveMode,
        subGroup,
        resetPerOutputEntry,
        patchPerOutputEntry,
    ]);

    useEffect(() => {
        labelRequestIdRef.current += 1;
        relationsRequestIdRef.current += 1;
    }, [uploadedFileName, uploadedFile]);

    // Auto-select default columns for HuggingFace datasets based on task type
    useEffect(() => {
        if (datasetId && columns && columns.length > 0 && !columnsLoading) {
            const defaultColumns = findDefaultColumns(columns, taskType);
            // Only auto-select label column if not already selected
            if (!selectedLabelColumn && defaultColumns.labelColumn && onLabelColumnChange) {
                onLabelColumnChange(defaultColumns.labelColumn);
            }
            // Note: Relations column auto-selection for HuggingFace datasets is not yet fully supported
            // Relations auto-selection is handled for uploaded files in fetchFileColumns
        }
    }, [datasetId, columns, columnsLoading, selectedLabelColumn, taskType, onLabelColumnChange]);

    const labelRequestIdRef = useRef(0);
    const relationsRequestIdRef = useRef(0);
    const delimiterDetectedForRef = useRef({});

    // State for uploaded file columns and labels
    const [fileColumns, setFileColumns] = useState([]);
    const [fileColumnsLoading, setFileColumnsLoading] = useState(false);
    const [fileSelectedLabelColumn, setFileSelectedLabelColumn] = useState(null);
    // The originally-detected/selected label column, retained even when the
    // "Does your data contain labels?" toggle is turned OFF. Classification only:
    // used to snapshot the file's original labels into an `origin_labels` extra column when
    // uploading without labels.
    const [detectedLabelColumn, setDetectedLabelColumn] = useState(null);
    const [fileSelectedTextColumn, setFileSelectedTextColumn] = useState(null);
    const [fileSelectedRelationsColumn, setFileSelectedRelationsColumn] = useState(null);
    const [fileExtractedLabels, setFileExtractedLabels] = useState([]);
    const [fileLabelsLoading, setFileLabelsLoading] = useState(false);
    const [fileLabelMappings, setFileLabelMappings] = useState({});
    const [fileLabelExamples, setFileLabelExamples] = useState({});
    // Single-label classification confidence-threshold fallback: the (post-mapping)
    // label designated as the "Other" catch-all. Only one label dataset-wide.
    const [fileRejectionLabelName, setFileRejectionLabelName] = useState(null);
    // Multilabel classification only: how to handle rows with an empty label
    // cell. 'none_selected' -> [""] (default); 'unlabeled' -> []; 'exclude' ->
    // skip the row entirely.
    const [emptyMultilabelHandling, setEmptyMultilabelHandling] = useState('none_selected');
    const [fileLabelError, setFileLabelError] = useState('');
    const [labelLimitExceeded, setLabelLimitExceeded] = useState(false);
    const [labelLimitSamples, setLabelLimitSamples] = useState([]);
    const [hasAttemptedSubmission, setHasAttemptedSubmission] = useState(false);

    // State for relations (for NER_WITH_RELATIONS tasks)
    const [fileExtractedRelations, setFileExtractedRelations] = useState([]);
    const [fileRelationsLoading, setFileRelationsLoading] = useState(false);
    const [fileRelationsMappings, setFileRelationsMappings] = useState({});

    // State for extracted summary (for summarization tasks)
    const [extractedSummary, setExtractedSummary] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(false);

    // State for prompt template (Step 3 - Review examples)
    const [examplesAreWithPrompt, setExamplesAreWithPrompt] = useState(false);
    const [promptTemplate, setPromptTemplate] = useState('');
    const [selectedDynamicFields, setSelectedDynamicFields] = useState([]);
    const [fieldOriginalTextMap, setFieldOriginalTextMap] = useState({});
    const [exampleDynamicField, setExampleDynamicField] = useState(null);
    // True once the user explicitly changed/cleared the example dynamic field,
    // so the auto-default (first dynamic field) stops overriding their choice.
    const exampleDynamicFieldTouchedRef = useRef(false);
    const [contentField, setContentField] = useState(null);
    const [exampleField, setExampleField] = useState(null);

    // Default the example dynamic field to the first dynamic field in the
    // template. The user can change it or clear it (an empty value blocks the
    // "Next" button on the Review examples step).
    useEffect(() => {
        if (!examplesAreWithPrompt || exampleDynamicField || exampleDynamicFieldTouchedRef.current) {
            return;
        }
        const templateFields = promptTemplate
            ? (promptTemplate.match(/\{\{(\w+)\}\}/g) || []).map(match => match.slice(2, -2))
            : [];
        const fields = [...new Set([...templateFields, ...selectedDynamicFields])];
        if (fields.length > 0) {
            setExampleDynamicField(fields[0]);
        }
    }, [examplesAreWithPrompt, promptTemplate, selectedDynamicFields, exampleDynamicField]);

    // Preloaded mode: sample record + distinct labels fetched from the raw dataset
    const [preloadedSampleRecord, setPreloadedSampleRecord] = useState(null);
    const [preloadedDistinctLabels, setPreloadedDistinctLabels] = useState([]);
    const [preloadedDistinctRelations, setPreloadedDistinctRelations] = useState([]);

    useEffect(() => {
        if (!visible || !isPreloaded || !clientId || !uploadedFileName) {
            setPreloadedSampleRecord(null);
            setPreloadedDistinctLabels([]);
            setPreloadedDistinctRelations([]);
            return;
        }
        data_from_server_post(
            'dataset-sample-record',
            { client_id: clientId, dataset_name: uploadedFileName, is_benchmark: isBenchmark },
            (response) => {
                setPreloadedSampleRecord(response?.sample || {});
                setPreloadedDistinctLabels(response?.distinct_labels || []);
                setPreloadedDistinctRelations(response?.distinct_relations || []);
            },
            () => {
                setPreloadedSampleRecord({});
                setPreloadedDistinctLabels([]);
                setPreloadedDistinctRelations([]);
            },
        );
    }, [visible, isPreloaded, clientId, uploadedFileName, isBenchmark]);

    const postJson = useCallback((endpoint, payload) => {
        return new Promise((resolve, reject) => {
            data_from_server_post(
                endpoint,
                payload,
                (response) => resolve(response),
                (error) => reject(new Error(error || 'Request failed'))
            );
        });
    }, []);

    const aiLabelRowsToMapping = useCallback(
        () => aiLabelRowsToMappingUtil(aiGeneratedLabels),
        [aiGeneratedLabels]
    );

    const uploadFileForLabels = useCallback(
        ({ labelColumn, delimiterValue, taskTypeValue = taskType, signal }) =>
            fetchFileLabelsWithUpload({
                file: uploadedFile,
                filename: uploadedFileName,
                clientId,
                labelColumn,
                fileType: uploadedFileType,
                taskType: taskTypeValue,
                delimiter: delimiterValue,
                signal,
            }),
        [uploadedFile, uploadedFileName, clientId, uploadedFileType, taskType]
    );

    const requestLabelsForColumn = useCallback(
        async (
            labelColumn,
            delimiterValue,
            taskTypeValue = taskType,
            preloadedDistinctTarget = null,
            compositeTaskKey = null,
            signal = undefined
        ) => {
            if (!labelColumn || !uploadedFileName || !clientId) {
                return null;
            }

            // Library / preloaded modal: no file bytes — use Mongo distincts on the raw collection.
            if (isPreloaded && !uploadedFile && !uploadedFileContent) {
                if (!preloadedDistinctTarget) {
                    return null;
                }
                try {
                    const payload = {
                        client_id: clientId,
                        dataset_name: uploadedFileName,
                        is_benchmark: isBenchmark,
                        distinct_target: preloadedDistinctTarget,
                        label_column: labelColumn,
                    };
                    if (compositeTaskKey) {
                        payload.composite_task_key = compositeTaskKey;
                    }
                    const res = await postJson('dataset-sample-record', payload);
                    const labels = Array.isArray(res?.distinct_labels) ? res.distinct_labels : [];
                    if (labels.length === 0) {
                        return {
                            success: false,
                            labels: [],
                            error:
                                res?.error ||
                                `No values found in stored dataset for the selected column.`,
                        };
                    }
                    return {
                        success: true,
                        labels,
                        label_examples: {},
                    };
                } catch (err) {
                    return {
                        success: false,
                        labels: [],
                        error: err?.message || 'Failed to load values from dataset.',
                    };
                }
            }

            if (!uploadedFileType) {
                return null;
            }

            if (uploadedFile) {
                return uploadFileForLabels({ labelColumn, delimiterValue, taskTypeValue, signal });
            }

            if (!uploadedFileContent) {
                return null;
            }

            return postJson("get-file-labels", {
                filename: uploadedFileName,
                client_id: clientId,
                label_column: labelColumn,
                file_content: uploadedFileContent,
                file_type: uploadedFileType,
                task_type: taskTypeValue,
                delimiter: delimiterValue,
            });
        },
        [
            uploadedFile,
            uploadedFileName,
            clientId,
            uploadedFileContent,
            uploadedFileType,
            taskType,
            uploadFileForLabels,
            postJson,
            isPreloaded,
            isBenchmark,
        ]
    );

    const fetchFileColumns = useCallback(async () => {
        if (!uploadedFileName || !clientId || !uploadedFileContent || !uploadedFileType) return;

        setFileColumnsLoading(true);
        setFileLabelError('');

        try {
            // Extract only the portion needed for column extraction
            const fileContentForColumns = extractFileContentForColumns(uploadedFileContent, uploadedFileType);

            data_from_server_post(
                "get-file-columns",
                {
                    filename: uploadedFileName,
                    client_id: clientId,
                    file_content: fileContentForColumns,
                    file_type: uploadedFileType
                },
                (response) => {
                    if (response.success && response.columns) {
                        setFileColumns(response.columns);

                        // Auto-select "text" column as example column if available
                        if (response.columns.includes('text')) {
                            setFileSelectedTextColumn('text');
                        } else if (response.columns.includes('example')) {
                            setFileSelectedTextColumn('example');
                        }

                        // Auto-select content field for step 3 (same as text column initially)
                        if (response.columns.includes('text')) {
                            setContentField('text');
                        } else if (response.columns.includes('example')) {
                            setContentField('example');
                        }
                        // Auto-select example field (same as text column initially)
                        if (response.columns.includes('text')) {
                            setExampleField('text');
                        } else if (response.columns.includes('example')) {
                            setExampleField('example');
                        }

                        // Auto-select default label/relations for single-objective uploads only.
                        // Composed tasks use per-usecase output state (ReviewOutputsStep).
                        if (!multiObjectiveMode) {
                            const defaultColumns = findDefaultColumns(response.columns, taskType);
                            if (defaultColumns.labelColumn) {
                                setFileSelectedLabelColumn(defaultColumns.labelColumn);
                                setDetectedLabelColumn(defaultColumns.labelColumn);
                            }
                            if (defaultColumns.relationsColumn) {
                                setFileSelectedRelationsColumn(defaultColumns.relationsColumn);
                            }
                        }
                    } else {
                        sparkToast.error(response.error || 'Failed to fetch file columns');
                        setFileColumns([]);
                    }
                    setFileColumnsLoading(false);
                },
                (error) => {
                    console.error('Error fetching file columns:', error);
                    sparkToast.error('Failed to fetch file columns');
                    setFileColumns([]);
                    setFileColumnsLoading(false);
                }
            );
        } catch (error) {
            console.error('Error fetching file columns:', error);
            setFileColumns([]);
            setFileColumnsLoading(false);
        }
    }, [
        uploadedFileName,
        clientId,
        uploadedFileContent,
        uploadedFileType,
        taskType,
        multiObjectiveMode,
    ]);

    const decodeBase64Unicode = useCallback((base64String) => {
        try {
            const binaryString = atob(base64String);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
            return new TextDecoder('utf-8').decode(bytes);
        } catch (error) {
            console.error('Error decoding base64:', error);
            throw error;
        }
    }, []);

    const encodeBase64Unicode = useCallback((text) => {
        try {
            const bytes = new TextEncoder().encode(text);
            const binaryString = Array.from(bytes, byte => String.fromCharCode(byte)).join('');
            return btoa(binaryString);
        } catch (error) {
            console.error('Error encoding to base64:', error);
            throw error;
        }
    }, []);

    /**
     * Extract a minimal chunk that contains header + first CSV record (quote-aware),
     * or a bounded JSON chunk for first-object parsing.
     */
    const extractFirstRecordChunk = useCallback((fullContent, fileType) => {
        if (!fullContent) return null;

        try {
            if (fileType === 'csv') {
                const initialDecodedBytes = 64 * 1024;
                const maxDecodedBytes = 8 * 1024 * 1024;
                let decodedBytes = initialDecodedBytes;

                while (decodedBytes <= maxDecodedBytes) {
                    const base64ChunkSize = Math.ceil(decodedBytes * 4 / 3);
                    const chunk = fullContent.substring(
                        0,
                        Math.min(base64ChunkSize, fullContent.length)
                    );
                    const paddedChunk =
                        chunk + '='.repeat((4 - (chunk.length % 4)) % 4);

                    let decodedChunk;
                    try {
                        decodedChunk = decodeBase64Unicode(paddedChunk);
                    } catch {
                        decodedBytes *= 2;
                        continue;
                    }

                    const csvEndIdx = findCsvEndIndexForRowCount(decodedChunk, 2);
                    if (csvEndIdx !== -1) {
                        return encodeBase64Unicode(decodedChunk.substring(0, csvEndIdx));
                    }

                    if (chunk.length >= fullContent.length) {
                        return encodeBase64Unicode(decodedChunk);
                    }

                    decodedBytes *= 2;
                }

                const fallbackBase64Size = Math.ceil(maxDecodedBytes * 4 / 3);
                const fallbackChunk = fullContent.substring(
                    0,
                    Math.min(fallbackBase64Size, fullContent.length)
                );
                return fallbackChunk + '='.repeat((4 - (fallbackChunk.length % 4)) % 4);
            }

            // JSON fallback: 1MB decoded preview
            const estimatedBytesNeeded = 1024 * 1024;
            const base64ChunkSize = Math.ceil(estimatedBytesNeeded * 4 / 3);
            const chunk = fullContent.substring(0, Math.min(base64ChunkSize, fullContent.length));
            return chunk + '='.repeat((4 - (chunk.length % 4)) % 4);
        } catch (error) {
            console.error('Error extracting first record chunk:', error);
            const safeChunkSize = Math.min(8192, fullContent.length);
            return fullContent.substring(0, safeChunkSize);
        }
    }, [decodeBase64Unicode, encodeBase64Unicode]);

    /**
     * Try to find the first complete JSON object in a (possibly truncated) string
     * by tracking brace depth. Returns the parsed object or null.
     */
    const parseFirstJsonObject = useCallback((text) => {
        const arrayStart = text.indexOf('[');
        const searchStart = arrayStart !== -1 ? arrayStart + 1 : 0;

        let braceCount = 0;
        let inString = false;
        let escapeNext = false;
        let objectStart = -1;

        for (let i = searchStart; i < text.length; i++) {
            const char = text[i];

            if (escapeNext) { escapeNext = false; continue; }
            if (char === '\\') { escapeNext = true; continue; }
            if (char === '"') { inString = !inString; continue; }

            if (!inString) {
                if (char === '{') {
                    if (braceCount === 0) objectStart = i;
                    braceCount++;
                } else if (char === '}') {
                    braceCount--;
                    if (braceCount === 0 && objectStart !== -1) {
                        try {
                            return JSON.parse(text.substring(objectStart, i + 1));
                        } catch { return null; }
                    }
                }
            }
        }
        return null;
    }, []);

    /**
     * Helper function to extract a field value from the first record.
     */
    const extractFieldValueFromFirstRecord = useCallback(async (fieldName) => {
        if (!fieldName) return null;

        if (preloadedSampleRecord) {
            const nested = getFieldValue(preloadedSampleRecord, fieldName);
            if (nested === undefined || nested === null) return null;
            return formatFieldPreview(nested);
        }

        if (!uploadedFileContent || !uploadedFileType) {
            return null;
        }

        try {
            let fieldValue = null;
            const contentChunk = extractFirstRecordChunk(uploadedFileContent, uploadedFileType);
            if (!contentChunk) return null;

            if (isJsonLikeFormat(uploadedFileType) || uploadedFileType === 'application/json') {
                const decoded = decodeBase64Unicode(contentChunk);

                let jsonData;
                try {
                    jsonData = JSON.parse(decoded);
                } catch {
                    if (uploadedFileType === 'jsonl') {
                        jsonData = parseFirstJsonlObject(decoded);
                    } else {
                        const firstObj = parseFirstJsonObject(decoded);
                        if (firstObj) jsonData = [firstObj];
                    }
                }

                if (Array.isArray(jsonData) && jsonData.length > 0) {
                    fieldValue = getFieldValue(jsonData[0], fieldName);
                } else if (jsonData && typeof jsonData === 'object') {
                    fieldValue = getFieldValue(jsonData, fieldName);
                }
            } else if (uploadedFileType === 'csv') {
                const decoded = decodeBase64Unicode(contentChunk);
                fieldValue = getCsvFieldValueFromFirstRecord(decoded, fieldName);
            }

            if (fieldValue === undefined || fieldValue === null) return null;
            return formatFieldPreview(fieldValue);
        } catch (error) {
            console.error('Error extracting field value from first record:', error);
            return null;
        }
    }, [preloadedSampleRecord, uploadedFileContent, uploadedFileType, extractFirstRecordChunk, decodeBase64Unicode, parseFirstJsonObject]);

    const patchMultiObjectiveEntry = useCallback(
        async (usecaseId, patch) => {
            const usecases = subGroup?.usecases || [];
            const usecase = usecases.find(
                (uc) => String(uc._id) === String(usecaseId)
            );
            const currentEntry = getPerOutputEntry(usecaseId);
            const nextEntry = { ...currentEntry, ...patch };

            patchPerOutputEntry(usecaseId, patch);
            if (!usecase) return;

            const rawCompositeKey =
                multiObjectiveMode && usecase
                    ? usecase.usecase_name || usecase._id
                    : null;
            const compositeTaskKeyForRow =
                rawCompositeKey != null && String(rawCompositeKey).trim() !== ''
                    ? String(rawCompositeKey)
                    : null;

            const hasOwn = (key) => Object.prototype.hasOwnProperty.call(patch, key);
            const selectedTaskType = usecase.task_type;

            if (
                isGenerativeTask(selectedTaskType) &&
                (hasOwn('labelColumn') || hasOwn('summaryColumn'))
            ) {
                const summaryColumn = nextEntry.summaryColumn || nextEntry.labelColumn;
                if (!summaryColumn) {
                    patchPerOutputEntry(usecaseId, { extractedSummary: null, summaryLoading: false });
                    return;
                }
                patchPerOutputEntry(usecaseId, { summaryLoading: true });
                try {
                    const summary = await extractFieldValueFromFirstRecord(summaryColumn);
                    patchPerOutputEntry(usecaseId, { extractedSummary: summary, summaryLoading: false });
                } catch {
                    patchPerOutputEntry(usecaseId, { extractedSummary: null, summaryLoading: false });
                }
                return;
            }

            if (
                hasOwn('labelColumn') ||
                (selectedTaskType === TaskType.classification && hasOwn('delimiter'))
            ) {
                const labelColumn = nextEntry.labelColumn;
                if (!labelColumn) {
                    patchPerOutputEntry(usecaseId, {
                        extractedLabels: [],
                        labelMappings: {},
                        labelExamples: {},
                    });
                } else {
                    const isMultilabelTask = selectedTaskType === TaskType.classification && !!usecase.is_multilabel;
                    const delimiterValue = isMultilabelTask ? nextEntry.delimiter : undefined;
                    if (isMultilabelTask && !delimiterValue) {
                        patchPerOutputEntry(usecaseId, {
                            extractedLabels: [],
                            labelMappings: {},
                            labelExamples: {},
                            labelsLoading: false,
                        });
                    } else {
                        patchPerOutputEntry(usecaseId, {
                            labelsLoading: true,
                        });
                        try {
                            let preloadedDistinctTarget = null;
                            if (isPreloaded && !uploadedFile && !uploadedFileContent) {
                                if (selectedTaskType === TaskType.classification) {
                                    preloadedDistinctTarget = 'classification';
                                } else if (
                                    selectedTaskType === TaskType.NER ||
                                    selectedTaskType === TaskType.NER_WITH_RELATIONS
                                ) {
                                    preloadedDistinctTarget = 'ner_entities';
                                }
                            }
                            const response = await requestLabelsForColumn(
                                labelColumn,
                                delimiterValue,
                                selectedTaskType,
                                preloadedDistinctTarget,
                                compositeTaskKeyForRow
                            );
                            if (response?.success && Array.isArray(response.labels)) {
                                const initialMappings = {};
                                response.labels.forEach((label) => {
                                    initialMappings[label] = label;
                                });
                                patchPerOutputEntry(usecaseId, {
                                    extractedLabels: response.labels,
                                    labelMappings: initialMappings,
                                    labelExamples: response.label_examples || {},
                                    labelsLoading: false,
                                });
                            } else {
                                patchPerOutputEntry(usecaseId, {
                                    extractedLabels: [],
                                    labelMappings: {},
                                    labelExamples: {},
                                    labelsLoading: false,
                                });
                            }
                        } catch {
                            patchPerOutputEntry(usecaseId, {
                                extractedLabels: [],
                                labelMappings: {},
                                labelExamples: {},
                                labelsLoading: false,
                            });
                        }
                    }
                }
            }

            if (hasOwn('relationsColumn')) {
                const relationsColumn = nextEntry.relationsColumn;
                if (!relationsColumn) {
                    patchPerOutputEntry(usecaseId, {
                        extractedRelations: [],
                        relationsMappings: {},
                        relationsLoading: false,
                    });
                } else {
                    patchPerOutputEntry(usecaseId, {
                        relationsLoading: true,
                    });
                    try {
                        const response = await requestLabelsForColumn(
                            relationsColumn,
                            undefined,
                            TaskType.NER_WITH_RELATIONS,
                            isPreloaded && !uploadedFile && !uploadedFileContent
                                ? 'ner_relations'
                                : null,
                            compositeTaskKeyForRow
                        );
                        if (response?.success && Array.isArray(response.labels)) {
                            const initialMappings = {};
                            response.labels.forEach((relation) => {
                                initialMappings[relation] = relation;
                            });
                            patchPerOutputEntry(usecaseId, {
                                extractedRelations: response.labels,
                                relationsMappings: initialMappings,
                                relationsLoading: false,
                            });
                        } else {
                            patchPerOutputEntry(usecaseId, {
                                extractedRelations: [],
                                relationsMappings: {},
                                relationsLoading: false,
                            });
                        }
                    } catch {
                        patchPerOutputEntry(usecaseId, {
                            extractedRelations: [],
                            relationsMappings: {},
                            relationsLoading: false,
                        });
                    }
                }
            }
        },
        [
            subGroup,
            patchPerOutputEntry,
            getPerOutputEntry,
            extractFieldValueFromFirstRecord,
            requestLabelsForColumn,
            isPreloaded,
            uploadedFile,
            uploadedFileContent,
            multiObjectiveMode,
        ]
    );

    patchMultiObjectiveEntryRef.current = patchMultiObjectiveEntry;

    useEffect(() => {
        if (
            !visible ||
            !multiObjectiveMode ||
            fileColumnsLoading ||
            !fileColumns.length ||
            !uploadedFileName
        ) {
            return undefined;
        }
        const key = `${uploadedFileName}|${fileColumns.join('\x1e')}`;
        if (appliedMultiObjectiveDefaultsKeyRef.current === key) {
            return undefined;
        }

        let cancelled = false;
        const scheduleId = window.setTimeout(() => {
            if (cancelled) return;
            if (appliedMultiObjectiveDefaultsKeyRef.current === key) return;

            const usecases = subGroup?.usecases || [];
            const perOutput = multiObjectivePerOutputRef.current || {};
            usecases.forEach((uc) => {
                const entry = perOutput[uc._id] || {};
                const defaults = findDefaultColumns(fileColumns, uc.task_type);
                const patch = {};
                if (isGenerativeTask(uc.task_type)) {
                    const col = defaults.labelColumn;
                    if (col && !entry.labelColumn && !entry.summaryColumn) {
                        patch.labelColumn = col;
                        patch.summaryColumn = col;
                    }
                } else if (uc.task_type === TaskType.classification) {
                    if (defaults.labelColumn && !entry.labelColumn) {
                        patch.labelColumn = defaults.labelColumn;
                    }
                } else if (
                    uc.task_type === TaskType.NER ||
                    uc.task_type === TaskType.NER_WITH_RELATIONS
                ) {
                    if (defaults.labelColumn && !entry.labelColumn) {
                        patch.labelColumn = defaults.labelColumn;
                    }
                    if (
                        uc.task_type === TaskType.NER_WITH_RELATIONS &&
                        defaults.relationsColumn &&
                        !entry.relationsColumn
                    ) {
                        patch.relationsColumn = defaults.relationsColumn;
                    }
                }
                if (Object.keys(patch).length > 0) {
                    patchMultiObjectiveEntryRef.current(uc._id, patch);
                }
            });
            appliedMultiObjectiveDefaultsKeyRef.current = key;
        }, 0);

        return () => {
            cancelled = true;
            window.clearTimeout(scheduleId);
        };
    }, [
        visible,
        multiObjectiveMode,
        fileColumnsLoading,
        fileColumns,
        uploadedFileName,
        subGroup,
    ]);

    /**
     * Fetch extracted summary for summarization task type
     */
    const fetchExtractedSummary = useCallback(async () => {
        if (
            !isGenerativeTask(taskType) ||
            !fileSelectedLabelColumn
        ) {
            return;
        }
        const hasFileData = uploadedFileContent && uploadedFileType;
        const hasSampleData = !!preloadedSampleRecord;
        if (!hasFileData && !hasSampleData) return;

        const isTruncatedPreview = uploadedFile && uploadedFile.size > MAX_PREVIEW_FILE_SIZE_BYTES;
        if (isTruncatedPreview) {
            setFileLabelError(getPreviewErrorMessage(taskType));
        }

        setSummaryLoading(true);
        setExtractedSummary(null);

        try {
            const summary = await extractFieldValueFromFirstRecord(fileSelectedLabelColumn);
            setExtractedSummary(summary);
        } catch (error) {
            console.error('Error fetching extracted summary:', error);
            setExtractedSummary(null);
        } finally {
            setSummaryLoading(false);
        }
    }, [taskType, fileSelectedLabelColumn, uploadedFileContent, uploadedFileType, preloadedSampleRecord, extractFieldValueFromFirstRecord]);

    const fetchFileLabels = useCallback(async (signal) => {
        if (!fileSelectedLabelColumn || !uploadedFileName || !clientId) {
            return;
        }

        let preloadedDistinctTarget = null;
        if (isPreloaded && !uploadedFile && !uploadedFileContent) {
            if (taskType === TaskType.classification) {
                preloadedDistinctTarget = 'classification';
            } else if (
                taskType === TaskType.NER ||
                taskType === TaskType.NER_WITH_RELATIONS
            ) {
                preloadedDistinctTarget = 'ner_entities';
            }
        }
        const fileBacked = (uploadedFileContent || uploadedFile) && uploadedFileType;
        if (!fileBacked && !preloadedDistinctTarget) {
            return;
        }

        // Skip label fetching for generative task types (summary/response text, no labels)
        if (isGenerativeTask(taskType)) {
            return;
        }

        const isTruncatedPreview = uploadedFile && uploadedFile.size > MAX_PREVIEW_FILE_SIZE_BYTES;

        // If multilabel classification, wait for delimiter selection
        const needsDelimiter = taskType === TaskType.classification && isMultilabel;
        if (needsDelimiter && !effectiveDelimiter) return;

        const requestId = ++labelRequestIdRef.current;
        setFileLabelsLoading(true);
        setFileLabelError(isTruncatedPreview ? getPreviewErrorMessage(taskType) : '');

        const delimiterValue = needsDelimiter ? effectiveDelimiter : undefined;

        try {
            const response = await requestLabelsForColumn(
                fileSelectedLabelColumn,
                delimiterValue,
                taskType,
                preloadedDistinctTarget,
                null,
                signal
            );

            if (labelRequestIdRef.current !== requestId) {
                return;
            }

            const totalFromBackend = response?.total_label_count;
            const exceededLimit = totalFromBackend && totalFromBackend > MAX_LABELS_TO_DISPLAY;

            if (exceededLimit) {
                const sampleValues = Array.isArray(response.labels) ? response.labels : [];
                const pluralLabel = getLabelColumnPluralLabel(taskType);
                setFileExtractedLabels([]);
                setFileLabelMappings({});
                setFileLabelExamples({});
                setLabelLimitExceeded(true);
                setLabelLimitSamples(sampleValues);
                setFileLabelError(
                    `Found ${totalFromBackend} unique ${pluralLabel} in the selected column. ` +
                    `Maximum allowed is ${MAX_LABELS_TO_DISPLAY}. ` +
                    `This usually means the wrong column was selected.`
                );
                setFileLabelsLoading(false);
                return;
            }

            if (response?.success && Array.isArray(response.labels)) {
                if (response.labels.length > MAX_LABELS_TO_DISPLAY) {
                    const total = response.labels.length;
                    setFileExtractedLabels([]);
                    setFileLabelMappings({});
                    setFileLabelExamples({});
                    setLabelLimitExceeded(true);
                    setLabelLimitSamples(response.labels.slice(0, 5));
                    const pluralLabel = getLabelColumnPluralLabel(taskType);
                    setFileLabelError(
                        `Found ${total} unique ${pluralLabel}. Maximum allowed is ${MAX_LABELS_TO_DISPLAY}. ` +
                        `This usually means the wrong column was selected.`
                    );
                    setFileLabelsLoading(false);
                    return;
                }

                setLabelLimitExceeded(false);
                setLabelLimitSamples([]);
                setFileExtractedLabels(response.labels);

                const initialMappings = {};
                response.labels.forEach(label => {
                    initialMappings[label] = label;
                });
                setFileLabelMappings(initialMappings);
                // Pre-select the task's previously-saved "Other"/rejection
                // fallback label when that label is present in this file, so
                // the choice persists across uploads. The checkbox row only
                // checks when its effective (post-mapping) label matches, so a
                // stale name that isn't in this file is harmless.
                setFileRejectionLabelName(existingRejectionLabelName || null);
                setFileLabelExamples(response.label_examples || {});
                setFileLabelError(isTruncatedPreview ? getPreviewErrorMessage(taskType) : '');
            } else {
                if (labelRequestIdRef.current !== requestId) {
                    return;
                }
                setLabelLimitExceeded(false);
                setLabelLimitSamples([]);
                setFileExtractedLabels([]);
                setFileLabelMappings({});
                setFileLabelExamples({});
                setFileLabelError(
                    response?.error || 'Failed to fetch labels. Choose a different label column.'
                );
            }
        } catch (error) {
            if (error?.name === 'AbortError') return;
            console.error('Error fetching file labels:', error);
            if (labelRequestIdRef.current !== requestId) {
                return;
            }
            setFileExtractedLabels([]);
            setFileLabelMappings({});
            setFileLabelExamples({});
            if (uploadedFile && uploadedFile.size > MAX_PREVIEW_FILE_SIZE_BYTES) {
                setFileLabelError(getPreviewErrorMessage(taskType));
            } else {
                setFileLabelError(
                    error?.message ||
                    'Failed to fetch labels from file. You can continue with the upload without reviewing labels.'
                );
            }
        } finally {
            if (labelRequestIdRef.current === requestId) {
                setFileLabelsLoading(false);
            }
        }
    }, [
        fileSelectedLabelColumn,
        uploadedFileName,
        clientId,
        uploadedFileContent,
        uploadedFile,
        uploadedFileType,
        effectiveDelimiter,
        taskType,
        isMultilabel,
        requestLabelsForColumn,
        isPreloaded,
    ]);

    const fetchFileRelations = useCallback(async (signal) => {
        if (!fileSelectedRelationsColumn || !uploadedFileName || !clientId) {
            return;
        }

        const preloadedRelations =
            isPreloaded &&
            !uploadedFile &&
            !uploadedFileContent &&
            taskType === TaskType.NER_WITH_RELATIONS;
        const fileBacked = (uploadedFileContent || uploadedFile) && uploadedFileType;
        if (!fileBacked && !preloadedRelations) {
            return;
        }

        const isTruncatedPreview = uploadedFile && uploadedFile.size > MAX_PREVIEW_FILE_SIZE_BYTES;

        const requestId = ++relationsRequestIdRef.current;
        setFileRelationsLoading(true);
        if (isTruncatedPreview) {
            setFileLabelError(getPreviewErrorMessage(taskType));
        }

        try {
            const response = await requestLabelsForColumn(
                fileSelectedRelationsColumn,
                undefined,
                TaskType.NER_WITH_RELATIONS,
                preloadedRelations ? 'ner_relations' : null,
                null,
                signal
            );

            if (relationsRequestIdRef.current !== requestId) {
                return;
            }

            const totalFromBackend = response?.total_label_count;
            const exceededLimit = totalFromBackend && totalFromBackend > MAX_LABELS_TO_DISPLAY;

            if (exceededLimit) {
                const sampleValues = Array.isArray(response.labels) ? response.labels : [];
                setFileExtractedRelations([]);
                setFileRelationsMappings({});
                setLabelLimitExceeded(true);
                setLabelLimitSamples(sampleValues);
                setFileLabelError(
                    `Found ${totalFromBackend} unique relations in the selected column. ` +
                    `Maximum allowed is ${MAX_LABELS_TO_DISPLAY}. ` +
                    `This usually means the wrong column was selected.`
                );
                setFileRelationsLoading(false);
                setFileLabelsLoading(false);
                return;
            }

            if (response?.success && Array.isArray(response.labels)) {
                if (response.labels.length > MAX_LABELS_TO_DISPLAY) {
                    const total = response.labels.length;
                    setFileExtractedRelations([]);
                    setFileRelationsMappings({});
                    setLabelLimitExceeded(true);
                    setLabelLimitSamples(response.labels.slice(0, 5));
                    setFileLabelError(
                        `Found ${total} unique relations. Maximum allowed is ${MAX_LABELS_TO_DISPLAY}. ` +
                        `This usually means the wrong column was selected.`
                    );
                    setFileRelationsLoading(false);
                    setFileLabelsLoading(false);
                    return;
                }

                setLabelLimitExceeded(false);
                setLabelLimitSamples([]);
                setFileExtractedRelations(response.labels);

                const initialMappings = {};
                response.labels.forEach(relation => {
                    initialMappings[relation] = relation;
                });
                setFileRelationsMappings(initialMappings);
                if (isTruncatedPreview) {
                    setFileLabelError(getPreviewErrorMessage(taskType));
                }
            } else {
                if (relationsRequestIdRef.current !== requestId) {
                    return;
                }
                setFileExtractedRelations([]);
                setFileRelationsMappings({});
                if (response?.error) {
                    setFileLabelError(response.error);
                }
            }
        } catch (error) {
            if (error?.name === 'AbortError') return;
            console.error('Error fetching file relations:', error);
            if (relationsRequestIdRef.current !== requestId) {
                return;
            }
            setFileExtractedRelations([]);
            setFileRelationsMappings({});
            if (uploadedFile && uploadedFile.size > MAX_PREVIEW_FILE_SIZE_BYTES) {
                setFileLabelError(getPreviewErrorMessage(taskType));
            } else if (error?.message) {
                setFileLabelError(error.message);
            }
        } finally {
            if (relationsRequestIdRef.current === requestId) {
                setFileRelationsLoading(false);
            }
        }
    }, [
        fileSelectedRelationsColumn,
        uploadedFileName,
        clientId,
        uploadedFileContent,
        uploadedFile,
        uploadedFileType,
        taskType,
        requestLabelsForColumn,
        isPreloaded,
    ]);

    // Fetch file columns when modal opens and we have an uploaded file
    useEffect(() => {
        if (visible && uploadedFileName && clientId && uploadedFileContent && uploadedFileType && currentStep === 1) {
            fetchFileColumns();
        }
    }, [visible, uploadedFileName, clientId, uploadedFileContent, uploadedFileType, currentStep, fetchFileColumns]);

    // Update error message when labels or mappings change (only after first submission attempt)
    const updateStep2Error = useCallback(() => {
        // Only update error message if user has already attempted submission
        // And we're NOT on step 1 (Review examples) - only update errors on the labels step
        if (!hasAttemptedSubmission || currentStep === 1) {
            return;
        }

        // Check if any final labels (after replacement) contain dots
        const finalLabels = fileExtractedLabels.map(label => {
            const replacement = fileLabelMappings[label];
            return replacement && replacement !== 'none' ? replacement : label;
        });

        const labelsWithDots = finalLabels.filter(label => label && label.includes('.'));
        if (labelsWithDots.length > 0) {
            setStep2Error(`Labels cannot contain "." character. Please replace labels: ${labelsWithDots.join(', ')}`);
        } else {
            setStep2Error('');
        }
    }, [fileExtractedLabels, fileLabelMappings, hasAttemptedSubmission, currentStep]);

    // Auto-detect the multilabel delimiter from raw label-column cells
    // (JSON/Python lists → 'list') so Data Overview uploads match the
    // create-task Data Collection wizard. Preview helpers unwrap lists and
    // would otherwise look like a comma delimiter.
    useEffect(() => {
        const sampleEntry = {
            content: uploadedFileContent,
            format: uploadedFileType,
            sampleRecord: preloadedSampleRecord,
        };
        if (!sampleEntry.content && !sampleEntry.sampleRecord) return undefined;

        const samplesForColumn = (column) => {
            if (!column) return [];
            return fieldValuesFromUploadEntry(sampleEntry, column, 20, { raw: true });
        };

        if (multiObjectiveMode) {
            (subGroup?.usecases || []).forEach((uc) => {
                if (uc.task_type !== TaskType.classification || !uc.is_multilabel) {
                    return;
                }
                const entry = getPerOutputEntry(uc._id);
                const column = entry?.labelColumn;
                if (!column || entry.delimiter) return;
                const values = samplesForColumn(column);
                if (!values.length) return;
                const signature = `${uc._id}::${column}::${uploadedFileName || ''}`;
                if (delimiterDetectedForRef.current[signature]) return;
                const detected = detectLabelsDelimiter(values);
                if (detected) {
                    patchMultiObjectiveEntryRef.current(uc._id, { delimiter: detected });
                } else {
                    delimiterDetectedForRef.current[signature] = true;
                }
            });
            return undefined;
        }

        if (!(taskType === TaskType.classification && isMultilabel)) return undefined;
        if (!fileSelectedLabelColumn || effectiveDelimiter) return undefined;
        const values = samplesForColumn(fileSelectedLabelColumn);
        if (!values.length) return undefined;
        const signature = `single::${fileSelectedLabelColumn}::${uploadedFileName || ''}`;
        if (delimiterDetectedForRef.current[signature]) return undefined;
        const detected = detectLabelsDelimiter(values);
        if (detected) {
            effectiveSetDelimiter(detected);
        } else {
            delimiterDetectedForRef.current[signature] = true;
        }
        return undefined;
    }, [
        multiObjectiveMode,
        subGroup,
        taskType,
        isMultilabel,
        fileSelectedLabelColumn,
        effectiveDelimiter,
        uploadedFileContent,
        uploadedFileType,
        preloadedSampleRecord,
        uploadedFileName,
        getPerOutputEntry,
        multiObjective.perOutput,
        effectiveSetDelimiter,
    ]);

    // Fetch labels when a label column is selected and delimiter (if required) is chosen.
    // Preloaded/library mode has no file bytes, but fetchFileLabels falls back to
    // dataset-sample-record distinct values from Mongo.
    useEffect(() => {
        if (
            visible &&
            currentStep === 2 &&
            fileSelectedLabelColumn &&
            uploadedFileName &&
            clientId
        ) {
            const controller = new AbortController();
            fetchFileLabels(controller.signal);
            return () => controller.abort();
        }
        return undefined;
    }, [
        visible,
        currentStep,
        fileSelectedLabelColumn,
        uploadedFileName,
        clientId,
        effectiveDelimiter,
        taskType,
        isMultilabel,
        uploadedFileType,
        isPreloaded,
        fetchFileLabels
    ]);

    // Fetch relations when a relations column is selected. In preloaded/library
    // mode this also uses Mongo distinct values instead of parsing file bytes.
    useEffect(() => {
        if (
            visible &&
            currentStep === 2 &&
            fileSelectedRelationsColumn &&
            uploadedFileName &&
            clientId &&
            taskType === "EntityRecognitionResponse"
        ) {
            const controller = new AbortController();
            fetchFileRelations(controller.signal);
            return () => controller.abort();
        }
        return undefined;
    }, [
        visible,
        currentStep,
        fileSelectedRelationsColumn,
        uploadedFileName,
        clientId,
        taskType,
        isPreloaded,
        fetchFileRelations
    ]);

    // Preloaded mode: populate labels/relations from the raw dataset's distinct values
    useEffect(() => {
        if (!isPreloaded || !visible || currentStep !== 2) return;

        if (preloadedDistinctLabels.length > 0 && fileSelectedLabelColumn) {
            setFileExtractedLabels(preloadedDistinctLabels);
            const initialMappings = {};
            preloadedDistinctLabels.forEach(label => { initialMappings[label] = label; });
            setFileLabelMappings(initialMappings);
            setFileLabelExamples({});
            setLabelLimitExceeded(false);
            setLabelLimitSamples([]);
            setFileLabelError('');
        }

        if (preloadedDistinctRelations.length > 0 && fileSelectedRelationsColumn) {
            setFileExtractedRelations(preloadedDistinctRelations);
            const initialMappings = {};
            preloadedDistinctRelations.forEach(r => { initialMappings[r] = r; });
            setFileRelationsMappings(initialMappings);
        }
    }, [
        isPreloaded,
        visible,
        currentStep,
        preloadedDistinctLabels,
        preloadedDistinctRelations,
        fileSelectedLabelColumn,
        fileSelectedRelationsColumn,
    ]);

    // Fetch extracted summary for summarization task type
    useEffect(() => {
        const hasFileData = uploadedFileContent && uploadedFileType;
        const hasSampleData = isPreloaded && preloadedSampleRecord;
        if (
            visible &&
            currentStep === 2 &&
            isGenerativeTask(taskType) &&
            fileSelectedLabelColumn &&
            (hasFileData || hasSampleData)
        ) {
            fetchExtractedSummary();
        }
    }, [
        visible,
        currentStep,
        taskType,
        fileSelectedLabelColumn,
        uploadedFileContent,
        uploadedFileType,
        isPreloaded,
        preloadedSampleRecord,
        fetchExtractedSummary
    ]);

    // Update error message when labels or mappings change
    useEffect(() => {
        if (fileExtractedLabels.length > 0 && Object.keys(fileLabelMappings).length > 0) {
            updateStep2Error();
        }
    }, [fileExtractedLabels, fileLabelMappings, updateStep2Error]);

    const handleFileLabelColumnChange = (value) => {
        setFileSelectedLabelColumn(value);
        if (
            taskType === TaskType.classification &&
            isMultilabel &&
            value !== fileSelectedLabelColumn
        ) {
            effectiveSetDelimiter(null);
        }
        if (value) {
            setDetectedLabelColumn(value);
        }
        setFileLabelError('');
        setStep2Error('');
        setLabelLimitExceeded(false);
        setLabelLimitSamples([]);
        setFileExtractedLabels([]);
        setFileLabelMappings({});
        setFileLabelExamples({});
        if (value) {
            setShowConfirmationStep(false);
        }
    };

    const handleFileTextColumnChange = (value) => {
        setFileSelectedTextColumn(value);
        setStep2Error('');
    };

    const handleContentFieldChange = (value) => {
        setContentField(value);
        setStep1Error('');
    };

    const handleExampleFieldChange = (value) => {
        setExampleField(value);
        setStep1Error('');
    };

    const handleExampleDynamicFieldChange = (value) => {
        exampleDynamicFieldTouchedRef.current = true;
        setExampleDynamicField(value);
        // Clear errors when user selects an example dynamic field
        if (value) {
            setStep1Error('');
            setStep2Error('');
        }
    };

    const handleFileRelationsColumnChange = (value) => {
        setFileSelectedRelationsColumn(value);
        setStep2Error('');
    };

    const handleFileDeleteLabel = (labelToDelete) => {
        setFileExtractedLabels(prev => prev.filter(label => label !== labelToDelete));
        // Mark the label as dropped (null) so the backend skips records carrying it,
        // instead of keeping the label as-is when no mapping is provided.
        setFileLabelMappings(prev => ({ ...prev, [labelToDelete]: null }));
        setFileLabelExamples(prev => {
            const nextExamples = { ...prev };
            delete nextExamples[labelToDelete];
            return nextExamples;
        });
    };

    const handleFileReplaceLabel = (oldLabel, newLabel) => {
        setFileLabelMappings(prev => ({
            ...prev,
            [oldLabel]: newLabel
        }));

        // Update error message in real-time
        updateStep2Error();
    };

    const handleFileAddNewLabel = (newLabel) => {
        setFileLabelMappings(prev => ({
            ...prev,
            [newLabel]: newLabel
        }));

        // Update error message in real-time
        updateStep2Error();
    };

    const handleFileDeleteRelation = (relationToDelete) => {
        setFileExtractedRelations(prev => prev.filter(relation => relation !== relationToDelete));
        // Mark as dropped (null) so the backend drops occurrences instead of preserving them.
        setFileRelationsMappings(prev => ({ ...prev, [relationToDelete]: null }));
    };

    const handleFileReplaceRelation = (oldRelation, newRelation) => {
        setFileRelationsMappings(prev => ({
            ...prev,
            [oldRelation]: newRelation
        }));
    };

    const handleFileAddNewRelation = (newRelation) => {
        setFileRelationsMappings(prev => ({
            ...prev,
            [newRelation]: newRelation
        }));
    };

    const handleHuggingFaceLabelColumnChange = (value) => {
        onLabelColumnChange && onLabelColumnChange(value);
        setStep2Error('');
    };

    const handleHuggingFaceTextColumnChange = (value) => {
        onTextColumnChange && onTextColumnChange(value);
        setStep2Error('');
    };

    const handleHuggingFaceRelationsColumnChange = (value) => {
        // For now, we'll just clear the error since we don't have a prop for this
        setStep2Error('');
    };

    const isStep2Valid = () => {
        const isHuggingFaceDataset = datasetId && columns;
        const isUploadedFile = uploadedFileName && clientId;

        if (isHuggingFaceDataset) {
            return selectedLabelColumn && selectedTextColumn;
        } else if (isUploadedFile) {
            // For uploaded files, labels are optional
            // Example column will be selected in step 3 if prompt instructions are enabled
            // Otherwise, we'll use a default
            return true;
        }
        return false;
    };

    const handleNext = () => {
        if (currentStep === 0 && !isPreloaded) {
            // Step 0 -> Step 1 (Review examples): Validate origin selection
            if (!selectedOrigin) {
                setOriginError('Please select a data creation to continue');
                return;
            }
            setOriginError('');
            setCurrentStep(1);
        } else if (currentStep === 1) {
            // Step 1 (Review examples) -> Step 2 (Review Labels)
            // For uploaded files, validate required fields
            const isUploadedFile = uploadedFileName && clientId;
            if (isUploadedFile) {
                // Validate based on toggle state
                if (examplesAreWithPrompt) {
                    // When toggle is ON, contentField is required
                    if (!contentField) {
                        setStep1Error('Please select a content field to continue');
                        return;
                    }
                    // When the template has dynamic fields, an example dynamic
                    // field must be selected before leaving this step.
                    if (computeDynamicFieldsList().length > 0 && !exampleDynamicField) {
                        setStep1Error('Missing example dynamic field');
                        return;
                    }
                    // The selected example dynamic field must exist in the
                    // prompt template (it can go stale if the user renames or
                    // removes its placeholder after selecting it).
                    if (exampleDynamicField && !computeTemplateFields().includes(exampleDynamicField)) {
                        setStep1Error(
                            `The example dynamic field '{{${exampleDynamicField}}}' does not exist in the prompt template. Update the template or select a different example dynamic field.`
                        );
                        return;
                    }
                } else {
                    // When toggle is OFF, exampleField is required
                    if (!exampleField) {
                        setStep1Error('Please select an example field to continue');
                        return;
                    }
                }
                setStep1Error('');
                setCurrentStep(2);
            } else {
                // For HuggingFace datasets, validate and proceed
                if (!isStep2Valid()) {
                    setStep2Error('Please select an example column to continue');
                    return;
                }
                onNext && onNext();
            }
        } else {
            onNext && onNext();
        }
    };

    const handleBack = () => {
        if (currentStep === 3) {
            setShowConfirmationStep(false);
            setCurrentStep(2);
        } else if (currentStep === 2 && !showConfirmationStep) {
            setCurrentStep(1);
        } else if (currentStep > stepOffset) {
            setCurrentStep(currentStep - 1);
            setHasAttemptedSubmission(false);
        } else if (isPreloaded) {
            // Library (preloaded) mode starts at the first visible step, so
            // there is no earlier step inside this modal. Back returns to the
            // "From Library" modal via the cancel path, which closes this
            // modal and reverts the pending selection so the user can change
            // their dataset/benchmark choices.
            handleCancel();
        }
    };

    const handleSkip = () => {
        // Auto-select "Other" as origin and proceed to step 1 (Review examples)
        if (onOriginChange) {
            onOriginChange('other');
        }
        setOriginError('');
        setCurrentStep(1);
    };

    // All dynamic fields the backend must know about: the union of fields
    // auto-detected in the template ({{field}} placeholders) and fields the
    // user created by selecting text. Sending only `selectedDynamicFields`
    // (previous behavior) dropped auto-detected fields, so a selected example
    // dynamic field could be missing from the list the data processor
    // receives — which made it skip every row and stall the upload at 0%.
    const computeTemplateFields = () => (
        promptTemplate
            ? (promptTemplate.match(/\{\{(\w+)\}\}/g) || []).map(match => match.slice(2, -2))
            : []
    );

    const computeDynamicFieldsList = () => (
        [...new Set([...computeTemplateFields(), ...selectedDynamicFields])]
    );

    const handleDone = () => {
        // Mark that user has attempted submission
        setHasAttemptedSubmission(true);

        // Step 1 (Review examples) - only for HuggingFace datasets
        if (currentStep === 1) {
            // For HuggingFace datasets, validate and proceed with upload
            if (!isStep2Valid()) {
                setStep2Error('Please complete the required fields to continue');
                return;
            }

            setStep2Error('');
            if (onDone) {
                const hfLabelMappings = labelMappings || {};
                const hfRelationsMappings = {};
                onDone(hfLabelMappings, selectedTextColumn, selectedLabelColumn, null, hfRelationsMappings, effectiveDelimiter);
            }
            return;
        }

        // Step 2 (Review Labels) - for uploaded files
        if (currentStep === 2 && !showConfirmationStep) {
            // Validate labels first
            if (!isStep2Valid()) {
                setStep2Error('Please complete the required fields to continue');
                return;
            }

            // Composed tasks store output columns per usecase in `perOutput`, not
            // `fileSelectedLabelColumn` (which stays empty in multi-objective mode).
            // The "any empty output" branch below covers multi-objective; the
            // single-objective branch keeps the legacy behavior keyed off
            // `fileSelectedLabelColumn`.
            const isUploadedFile = uploadedFileName && clientId;
            const hasNoLabels =
                isUploadedFile &&
                fileHasLabels &&
                !fileSelectedLabelColumn &&
                !multiObjectiveMode;

            if (hasNoLabels) {
                // Show confirmation step for files without labels (legacy path).
                setShowConfirmationStep(true);
                setCurrentStep(3);
                return;
            }

            // Multi-objective: if the per-tab validator surfaced any soft-empty
            // tab (a column that was left blank), route through the same
            // confirm-upload step instead of disabling Done.
            if (
                isUploadedFile &&
                multiObjectiveMode &&
                multiObjectiveValidation.ok &&
                multiObjectiveValidation.anyEmpty
            ) {
                setShowConfirmationStep(true);
                setCurrentStep(3);
                return;
            }

            // If user indicated the file has NO labels and chose generate-with-AI
            // without a catalog (class/NER), confirm before uploading.
            if (
                isUploadedFile &&
                !fileHasLabels &&
                !multiObjectiveMode &&
                fileNoLabelsMode === NO_LABELS_MODE_GENERATE_WITH_AI &&
                (taskType === TaskType.classification ||
                    taskType === TaskType.NER ||
                    taskType === TaskType.NER_WITH_RELATIONS)
            ) {
                const generatedMapping = aiLabelRowsToMapping();
                if (!generatedMapping || Object.keys(generatedMapping).length === 0) {
                    setShowConfirmationStep(true);
                    setCurrentStep(3);
                    return;
                }
            }

            // Unlabeled opt-out: confirm before proceeding without responses.
            if (
                isUploadedFile &&
                !fileHasLabels &&
                !multiObjectiveMode &&
                fileNoLabelsMode === NO_LABELS_MODE_UNLABELED
            ) {
                setShowConfirmationStep(true);
                setCurrentStep(3);
                return;
            }

            // Check if any final labels (after replacement) contain dots
            const finalLabels = fileExtractedLabels.map(label => {
                const replacement = fileLabelMappings[label];
                return replacement && replacement !== 'none' ? replacement : label;
            });

            const labelsWithDots = finalLabels.filter(label => label && label.includes('.'));
            if (labelsWithDots.length > 0) {
                setStep2Error(`Labels cannot contain "." character. Please replace labels: ${labelsWithDots.join(', ')}`);
                return;
            }

            // Validate prompt template/examples if enabled
            if (examplesAreWithPrompt) {
                if (!contentField) {
                    setStep2Error('Please select a content field');
                    return;
                }
            } else {
                // When toggle is OFF, validate that example field is selected
                if (!exampleField) {
                    setStep2Error('Please select an example field');
                    return;
                }
            }

            if (examplesAreWithPrompt) {
                if (!promptTemplate || !promptTemplate.trim()) {
                    setStep2Error('Please enter a prompt template');
                    return;
                }
                // The example dynamic field itself is validated when leaving
                // the "Review examples" step (see handleNext), so by this
                // point it is guaranteed to be selected.
            }

            setStep2Error('');
            // Pass the label mappings and prompt template data for uploaded files
            const submitAiCatalog =
                !fileHasLabels && fileNoLabelsMode === NO_LABELS_MODE_GENERATE_WITH_AI;
            const labelMappings = fileHasLabels
                ? fileLabelMappings
                : submitAiCatalog
                  ? aiLabelRowsToMapping()
                  : {};
            const relationsMappings = fileRelationsMappings;
            // Use content field if toggle is ON, exampleField if toggle is OFF, otherwise fall back
            const exampleColumnName = examplesAreWithPrompt
                ? (contentField || fileSelectedTextColumn || "example")
                : (exampleField || fileSelectedTextColumn || "example");
            const dynamicFieldsList = computeDynamicFieldsList();

            if (multiObjectiveMode && onDoneObject) {
                if (!multiObjectiveValidation.ok) {
                    setStep2Error('Please complete the required fields for every objective.');
                    return;
                }
                onDoneObject({
                    origin: { selectedOrigin },
                    examples: {
                        examplesAreWithPrompt,
                        promptTemplate,
                        dynamicFieldsList,
                        exampleDynamicField,
                        exampleColumnName,
                    },
                    outputs: multiObjective.perOutput,
                });
                return;
            }

            if (onDone) {
                const rejectionLabelNameToSend =
                    taskType === TaskType.classification && !isMultilabel
                        ? fileRejectionLabelName
                        : null;
                const generateResponsesWithAi =
                    !fileHasLabels &&
                    fileNoLabelsMode === NO_LABELS_MODE_GENERATE_WITH_AI &&
                    isGenerativeAiEmptyColumnTask(taskType);
                onDone(
                    labelMappings,
                    exampleColumnName, // The column name in the file that contains the prompt+example
                    fileHasLabels ? fileSelectedLabelColumn : "",
                    fileHasLabels ? fileSelectedRelationsColumn : null,
                    relationsMappings,
                    examplesAreWithPrompt,
                    promptTemplate,
                    dynamicFieldsList,
                    exampleDynamicField, // The dynamic field name like "example" from {{example}}
                    fileHasLabels || !submitAiCatalog ? "" : (detectedLabelColumn || ""),
                    null, // subtaskMappings (single-objective path)
                    rejectionLabelNameToSend,
                    emptyMultilabelHandling,
                    generateResponsesWithAi
                );
            }
        }
    };

    const handleConfirmUpload = () => {
        // Proceed with upload without labels
        setStep2Error('');

        // Use content field if toggle is ON, exampleField if toggle is OFF, otherwise fall back
        const exampleColumnName = examplesAreWithPrompt
            ? (contentField || fileSelectedTextColumn || "example")
            : (exampleField || fileSelectedTextColumn || "example");
        const dynamicFieldsList = computeDynamicFieldsList();

        // Multi-objective: forward the per-tab `perOutput` as-is. Tabs that
        // were left empty already have `labelColumn === null` and either
        // `aiLabels` rows (classification, hasLabels=false) or no mapping at
        // all — the consumer in `UploadDataTab` filters those out so only
        // tabs with a real column produce a `subtask_mappings` entry.
        if (multiObjectiveMode && onDoneObject) {
            onDoneObject({
                origin: { selectedOrigin },
                examples: {
                    examplesAreWithPrompt,
                    promptTemplate,
                    dynamicFieldsList,
                    exampleDynamicField,
                    exampleColumnName,
                },
                outputs: multiObjective.perOutput,
            });
            return;
        }

        if (onDone) {
            const submitAiCatalog =
                !fileHasLabels && fileNoLabelsMode === NO_LABELS_MODE_GENERATE_WITH_AI;
            const generateResponsesWithAi =
                submitAiCatalog && isGenerativeAiEmptyColumnTask(taskType);
            onDone(
                submitAiCatalog ? aiLabelRowsToMapping() : {},
                exampleColumnName,
                "", // No label column
                null,
                {},
                examplesAreWithPrompt,
                promptTemplate,
                dynamicFieldsList,
                exampleDynamicField,
                // Classification only: snapshot the file's original label column into `origin_labels`
                // when generating with AI (not plain unlabeled).
                submitAiCatalog && taskType === TaskType.classification
                    ? detectedLabelColumn || ""
                    : "",
                null, // subtaskMappings (single-objective path)
                null, // rejectionLabelName (not applicable when uploading without labels)
                emptyMultilabelHandling,
                generateResponsesWithAi
            );
        }
    };

    const handleCancel = () => {
        setCurrentStep(stepOffset);
        setOriginError('');
        setStep2Error('');
        setStep1Error('');
        setHasAttemptedSubmission(false);
        setShowConfirmationStep(false);
        labelRequestIdRef.current += 1;
        relationsRequestIdRef.current += 1;
        onCancel();
    };

    // Allow clicking steps:
    // - Always allow going backward
    // - Allow going forward only if prior steps are already complete
    const handleStepChange = (displayIndex) => {
        if (typeof displayIndex !== 'number') return;
        const nextStep = displayIndex + stepOffset;
        if (nextStep === currentStep) return;

        const isUploadedFile = !!(uploadedFileName && clientId);

        const step0Complete = isPreloaded || !!selectedOrigin;
        const missingExampleDynamicField =
            examplesAreWithPrompt && computeDynamicFieldsList().length > 0 && !exampleDynamicField;
        const staleExampleDynamicField =
            examplesAreWithPrompt && !!exampleDynamicField && !computeTemplateFields().includes(exampleDynamicField);
        const step1Complete = isUploadedFile
            ? (examplesAreWithPrompt
                ? (!!contentField && !missingExampleDynamicField && !staleExampleDynamicField)
                : !!exampleField)
            : true;

        // Gate forward navigation (no skipping ahead).
        if (nextStep > currentStep) {
            if (nextStep >= 1 && !step0Complete) {
                setOriginError('Please select a data creation method to continue');
                return;
            }

            if (nextStep >= 2 && !step1Complete) {
                if (isUploadedFile) {
                    if (examplesAreWithPrompt && !contentField) {
                        setStep1Error('Please select a content field to continue');
                    } else if (examplesAreWithPrompt && missingExampleDynamicField) {
                        setStep1Error('Missing example dynamic field');
                    } else if (examplesAreWithPrompt && staleExampleDynamicField) {
                        setStep1Error(
                            `The example dynamic field '{{${exampleDynamicField}}}' does not exist in the prompt template. Update the template or select a different example dynamic field.`
                        );
                    } else {
                        setStep1Error('Please select an example field to continue');
                    }
                }
                return;
            }
        }

        if (showConfirmationStep && nextStep !== 3) {
            setShowConfirmationStep(false);
        }

        setOriginError('');
        setStep1Error('');
        setStep2Error('');
        if (nextStep === 0) {
            setHasAttemptedSubmission(false);
        }
        setCurrentStep(nextStep);
    };

    // Determine steps dynamically based on whether labels are selected
    const getSteps = () => {
        const labelStepName = 'Review Outputs';
        const isUploadedFile = !!uploadedFileName;

        const baseSteps = isPreloaded
            ? ['Review examples', labelStepName]
            : ['Data Creation', 'Review examples', labelStepName];

        if (isUploadedFile && showConfirmationStep) {
            return [...baseSteps, 'Confirm Upload'];
        }
        return baseSteps;
    };

    const steps = getSteps();

    const renderStep1 = () => (
        <div style={{ marginBottom: '24px' }}>
            <div style={{
                fontSize: '14px',
                color: '#262626',
                marginBottom: '14px'
            }}>
                What best describes how this dataset was created?
            </div>

            <div role="radiogroup" style={{ width: '100%' }}>
                {[
                    { value: 'human_created', heading: 'Human created', description: 'A human manually created this dataset' },
                    { value: 'commercial_model', heading: 'LLM created', description: 'LLM created this dataset' },
                    { value: 'other', heading: 'Other', description: 'Human edited LLM or something else.' },
                ].map((option) => (
                    <div key={option.value} style={{ marginBottom: '16px' }}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                borderRadius: '8px',
                                width: '100%',
                                margin: 0,
                                cursor: 'pointer',
                            }}
                            onClick={() => {
                                onOriginChange && onOriginChange(option.value);
                                setOriginError('');
                            }}
                        >
                            <Radio
                                label=""
                                name="dataset-origin"
                                value={option.value}
                                status={selectedOrigin === option.value ? 'active' : 'inactive'}
                                onChange={(e) => {
                                    onOriginChange && onOriginChange(e.target.value);
                                    setOriginError('');
                                }}
                            />
                            <div style={{ marginLeft: '8px' }}>
                                <div style={{
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    color: '#262626',
                                    marginBottom: '4px'
                                }}>
                                    {option.heading}
                                </div>
                                <div style={{
                                    fontSize: '12px',
                                    color: '#8c8c8c',
                                    lineHeight: '1.4'
                                }}>
                                    {option.description}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            {originError && (
                <div style={{
                    color: '#ff4d4f',
                    fontSize: '12px',
                    marginTop: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                }}>
                    {originError}
                </div>
            )}
        </div>
    );

    const renderStep2 = () => {
        // Step 1 is now Review examples (for uploaded files)
        const isUploadedFile = uploadedFileName && clientId;

        // Show Review examples step for uploaded files
        if (isUploadedFile) {
            return (
                <PromptTemplateStep
                    examplesAreWithPrompt={examplesAreWithPrompt}
                    onExamplesAreWithPromptChange={(value) => {
                        setExamplesAreWithPrompt(value);
                        setStep1Error('');
                    }}
                    promptTemplate={promptTemplate}
                    onPromptTemplateChange={setPromptTemplate}
                    selectedDynamicFields={selectedDynamicFields}
                    onSelectedDynamicFieldsChange={setSelectedDynamicFields}
                    fieldOriginalTextMap={fieldOriginalTextMap}
                    onFieldOriginalTextMapChange={setFieldOriginalTextMap}
                    exampleDynamicField={exampleDynamicField}
                    onExampleDynamicFieldChange={handleExampleDynamicFieldChange}
                    columns={fileColumns}
                    columnsLoading={fileColumnsLoading}
                    selectedLabelColumn={fileSelectedLabelColumn}
                    contentField={contentField}
                    onContentFieldChange={handleContentFieldChange}
                    exampleField={exampleField}
                    onExampleFieldChange={handleExampleFieldChange}
                    uploadedFileName={uploadedFileName}
                    clientId={clientId}
                    uploadedFileContent={uploadedFileContent}
                    uploadedFileType={uploadedFileType}
                    onNeedHelpClick={onOpenHelp}
                    sampleRecord={isPreloaded ? preloadedSampleRecord : null}
                />
            );
        }

        return null;
    };

    const renderStep3 = () => {
        // Multi-objective Task mode: render the per-objective tab strip + forms
        // instead of the legacy single-task labels section. Validation state
        // is bubbled up via `setMultiObjectiveValidation` and gates `handleDone`.
        if (multiObjectiveMode) {
            const usecases = subGroup?.usecases || [];

            const handlersByUsecase = Object.fromEntries(
                usecases.map((uc) => [
                    String(uc._id),
                    {
                        onDeleteLabel: (labelToDelete) => {
                            const entry = multiObjective.getEntry(uc._id);
                            const nextExtracted = (entry.extractedLabels || []).filter(
                                (label) => label !== labelToDelete
                            );
                            // `null` is the contract with csv/json iterators: drop rows carrying
                            // this label (single-label) or strip the label (multilabel). Deleting
                            // the key would fall through to `.get(label, label)` and keep PER.
                            const nextMappings = {
                                ...(entry.labelMappings || {}),
                                [labelToDelete]: null,
                            };
                            const nextExamples = { ...(entry.labelExamples || {}) };
                            delete nextExamples[labelToDelete];
                            multiObjective.patchEntry(uc._id, {
                                extractedLabels: nextExtracted,
                                labelMappings: nextMappings,
                                labelExamples: nextExamples,
                            });
                        },
                        onReplaceLabel: (oldLabel, newLabel) => {
                            const entry = multiObjective.getEntry(uc._id);
                            multiObjective.patchEntry(uc._id, {
                                labelMappings: {
                                    ...(entry.labelMappings || {}),
                                    [oldLabel]: newLabel,
                                },
                            });
                        },
                        onAddNewLabel: (newLabel) => {
                            const entry = multiObjective.getEntry(uc._id);
                            multiObjective.patchEntry(uc._id, {
                                labelMappings: {
                                    ...(entry.labelMappings || {}),
                                    [newLabel]: newLabel,
                                },
                            });
                        },
                        onDeleteRelation: (relationToDelete) => {
                            const entry = multiObjective.getEntry(uc._id);
                            const nextExtracted = (entry.extractedRelations || []).filter(
                                (relation) => relation !== relationToDelete
                            );
                            const nextMappings = {
                                ...(entry.relationsMappings || {}),
                                [relationToDelete]: null,
                            };
                            multiObjective.patchEntry(uc._id, {
                                extractedRelations: nextExtracted,
                                relationsMappings: nextMappings,
                            });
                        },
                        onReplaceRelation: (oldRelation, newRelation) => {
                            const entry = multiObjective.getEntry(uc._id);
                            multiObjective.patchEntry(uc._id, {
                                relationsMappings: {
                                    ...(entry.relationsMappings || {}),
                                    [oldRelation]: newRelation,
                                },
                            });
                        },
                        onAddNewRelation: (newRelation) => {
                            const entry = multiObjective.getEntry(uc._id);
                            multiObjective.patchEntry(uc._id, {
                                relationsMappings: {
                                    ...(entry.relationsMappings || {}),
                                    [newRelation]: newRelation,
                                },
                            });
                        },
                    },
                ])
            );

            // Shared callback used by `AiGeneratedLabelsBlock` to seed the
            // LLM with examples from the uploaded file. Mirrors the legacy
            // single-objective path: prefers the prompt-template content
            // field when the toggle is ON, otherwise the example field.
            const getExamplesForAi = () => {
                const exampleFieldForSuggestions = examplesAreWithPrompt
                    ? (contentField || fileSelectedTextColumn || 'example')
                    : (exampleField || fileSelectedTextColumn || 'example');
                return getFieldValuesFromFirstNRecords({
                    base64Content: uploadedFileContent,
                    fileType: uploadedFileType,
                    fieldName: exampleFieldForSuggestions,
                    maxRecords: 6,
                });
            };

            const fileColumnsByUsecase = Object.fromEntries(
                usecases.map((uc) => {
                    const ucId = String(uc._id);
                    const labelTypeForUc =
                        uc.task_type === TaskType.classification
                            ? 'Label'
                            : isGenerativeTask(uc.task_type)
                                ? 'Summary'
                                : 'Entity';
                    const perUcOptions = labelsByUsecaseId[ucId];
                    const optionsForPick =
                        perUcOptions != null
                            ? perUcOptions
                            : String(llmUsecaseId) === ucId
                                ? allLabelOptions
                                : {};
                    return [
                        ucId,
                        {
                            ...(multiObjective.getEntry(uc._id) || {}),
                            columns: fileColumns || [],
                            columnsLoading: !!fileColumnsLoading,
                            extractedLabels: multiObjective.getEntry(uc._id)?.extractedLabels || [],
                            labelsLoading: !!multiObjective.getEntry(uc._id)?.labelsLoading,
                            labelExamples: multiObjective.getEntry(uc._id)?.labelExamples || {},
                            availableLabels:
                                pickAvailableLabelsFromOptions(optionsForPick, labelTypeForUc) || [],
                            extractedRelations: multiObjective.getEntry(uc._id)?.extractedRelations || [],
                            relationsLoading: !!multiObjective.getEntry(uc._id)?.relationsLoading,
                            extractedSummary: multiObjective.getEntry(uc._id)?.extractedSummary || null,
                            summaryLoading: !!multiObjective.getEntry(uc._id)?.summaryLoading,
                            getExamples: getExamplesForAi,
                        },
                    ];
                })
            );

            return (
                <ReviewOutputsStep
                    subGroup={subGroup}
                    perOutput={multiObjective.perOutput}
                    onPatchEntry={patchMultiObjectiveEntry}
                    fileColumnsByUsecase={fileColumnsByUsecase}
                    handlersByUsecase={handlersByUsecase}
                    onValidationChange={setMultiObjectiveValidation}
                    onOpenHelp={onOpenHelp}
                    activeUsecaseId={activeUsecaseId}
                    onActiveUsecaseChange={setActiveUsecaseId}
                />
            );
        }

        // Step 2 is now Review Labels (for uploaded files)
        const isUploadedFile = uploadedFileName && clientId;

        // Determine if we're working with a HuggingFace dataset or uploaded file
        const isHuggingFaceDataset = datasetId && columns;

        if (isHuggingFaceDataset) {
            // Render HuggingFace dataset review
            return (
                <div>
                    <ReviewExtractedLabels
                        datasetId={datasetId}
                        columns={columns}
                        loading={columnsLoading}
                        selectedLabelColumn={selectedLabelColumn}
                        selectedTextColumn={selectedTextColumn}
                        selectedRelationsColumn={null} // HuggingFace datasets don't have relations column state yet
                        onLabelColumnChange={handleHuggingFaceLabelColumnChange}
                        onTextColumnChange={handleHuggingFaceTextColumnChange}
                        onRelationsColumnChange={handleHuggingFaceRelationsColumnChange}
                        labelError={labelError}
                        taskType={taskType}
                        isMultilabel={isMultilabel}
                        delimiter={effectiveDelimiter}
                        setDelimiter={effectiveSetDelimiter}
                        selectedTaskType={taskType}
                    />

                    <LabelExtractionManager
                        labelColumn={selectedLabelColumn}
                        extractedLabels={extractedLabels}
                        loading={labelsLoading}
                        labelMappings={labelMappings}
                        onDeleteLabel={onDeleteLabel}
                        onReplaceLabel={onReplaceLabel}
                        onAddNewLabel={onAddNewLabel}
                        labelType={taskType === "EntityRecognitionResponse" ? "Entity" : taskType === "NERResponse" ? "Entity" : "Label"}
                        taskType={taskType}
                        isMultilabel={isMultilabel}
                        availableLabels={getAvailableLabelsForType(taskType === "EntityRecognitionResponse" ? "Entity" : taskType === "NERResponse" ? "Entity" : "Label")}
                    />
                    {Boolean(
                        taskType === TaskType.classification ||
                            taskType === TaskType.NER ||
                            taskType === 'EntityRecognitionResponse' ||
                            taskType === TaskType.summarization ||
                            taskType === TaskType.contentGeneration
                    ) && (
                        <EmptyResponsesChoice
                            isMultilabel={isMultilabel}
                            taskType={taskType}
                            value={emptyMultilabelHandling}
                            onChange={setEmptyMultilabelHandling}
                        />
                    )}
                    {step2Error && (
                        <div style={{
                            color: '#ff4d4f',
                            fontSize: '12px',
                            marginTop: '16px',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '4px',
                            wordBreak: 'break-all',
                            overflowWrap: 'break-word',
                            maxWidth: '100%',
                            whiteSpace: 'pre-wrap'
                        }}>
                            {step2Error}
                        </div>
                    )}
                </div>
            );
        } else if (isUploadedFile) {
            const isClassificationTask = taskType === TaskType.classification;
            const isNerTask =
                taskType === TaskType.NER || taskType === TaskType.NER_WITH_RELATIONS;
            const showAiCatalog =
                !fileHasLabels && fileNoLabelsMode === NO_LABELS_MODE_GENERATE_WITH_AI;
            return (
                <div>
                    <HasLabelsSwitch
                        taskType={taskType}
                        checked={fileHasLabels}
                        onChange={(checked) => {
                            setFileHasLabels(checked);
                            setFileNoLabelsMode(NO_LABELS_MODE_DEFAULT);
                            setStep2Error('');
                            setShowConfirmationStep(false);
                            if (!checked) {
                                // Explicit "no labels" mode: hide extracted-label flow
                                setFileSelectedLabelColumn(null);
                                setFileExtractedLabels([]);
                                setFileLabelMappings({});
                                setFileLabelExamples({});
                                setFileLabelError('');
                            } else {
                                // Back to "has labels" mode: clear AI suggestions
                                setAiGeneratedLabels([]);
                            }
                        }}
                    />

                    {!fileHasLabels ? (
                        <>
                            <NoLabelsModeChoice
                                taskType={taskType}
                                value={fileNoLabelsMode}
                                onChange={(mode) => {
                                    setFileNoLabelsMode(mode);
                                    if (mode === NO_LABELS_MODE_UNLABELED) {
                                        setAiGeneratedLabels([]);
                                    } else if (
                                        isNerTask &&
                                        (aiGeneratedLabels || []).length === 0
                                    ) {
                                        const seed = getAvailableLabelsForType('Entity');
                                        const unique = Array.from(
                                            new Set(
                                                (seed || [])
                                                    .map((l) => (l || '').trim())
                                                    .filter(Boolean)
                                            )
                                        );
                                        setAiGeneratedLabels(
                                            unique.length
                                                ? unique.map((name, idx) => ({
                                                      id: `ai_entity_${Date.now()}_${idx}`,
                                                      name,
                                                      replaceWith: name,
                                                      isNew: false,
                                                  }))
                                                : [makeEmptyAiLabelRow(0)]
                                        );
                                    }
                                }}
                            />
                            {showAiCatalog && isClassificationTask ? (
                                <AiGeneratedLabelsBlock
                                    clientId={clientId}
                                    usecaseId={llmUsecaseId}
                                    taskType={TaskType.classification}
                                    aiLabels={aiGeneratedLabels}
                                    onChange={(rows) => {
                                        if (step2Error) setStep2Error('');
                                        setAiGeneratedLabels(rows);
                                    }}
                                    availableLabels={getAvailableLabelsForType('Label')}
                                    getExamples={() => {
                                        const exampleFieldForSuggestions = examplesAreWithPrompt
                                            ? (contentField || fileSelectedTextColumn || 'example')
                                            : (exampleField || fileSelectedTextColumn || 'example');
                                        return getFieldValuesFromFirstNRecords({
                                            base64Content: uploadedFileContent,
                                            fileType: uploadedFileType,
                                            fieldName: exampleFieldForSuggestions,
                                            maxRecords: 6,
                                        });
                                    }}
                                    showRejectionColumn={
                                        taskType === TaskType.classification &&
                                        !isMultilabel &&
                                        confidenceRejectionEnabled
                                    }
                                    rejectionLabelName={fileRejectionLabelName}
                                    onSetRejectionLabel={setFileRejectionLabelName}
                                />
                            ) : null}
                            {showAiCatalog && isNerTask ? (
                                <div style={{ marginTop: 8 }}>
                                    <AIGeneratedLabelsManager
                                        title="Entity types for AI generation"
                                        labels={aiGeneratedLabels}
                                        availableLabels={getAvailableLabelsForType('Entity')}
                                        onChangeNewLabelName={(id, nextName) => {
                                            setAiGeneratedLabels((prev) =>
                                                (prev || []).map((row) =>
                                                    row.id === id
                                                        ? { ...row, name: nextName }
                                                        : row
                                                )
                                            );
                                        }}
                                        onChangeReplaceWith={(id, nextReplace) => {
                                            setAiGeneratedLabels((prev) =>
                                                (prev || []).map((row) => {
                                                    if (row.id !== id) return row;
                                                    const isNew = !!row?.isNew;
                                                    return {
                                                        ...row,
                                                        replaceWith: nextReplace,
                                                        name: isNew
                                                            ? row?.name
                                                            : row?.name
                                                              ? row.name
                                                              : nextReplace || '',
                                                    };
                                                })
                                            );
                                        }}
                                        onDeleteLabel={(id) =>
                                            setAiGeneratedLabels((prev) =>
                                                (prev || []).filter((r) => r.id !== id)
                                            )
                                        }
                                        onAddLabel={() =>
                                            setAiGeneratedLabels((prev) => [
                                                ...(prev || []),
                                                makeEmptyAiLabelRow((prev || []).length),
                                            ])
                                        }
                                    />
                                </div>
                            ) : null}
                        </>
                    ) : (
                        <ReviewExtractedLabels
                            datasetId={uploadedFileName}
                            columns={fileColumns}
                            loading={fileColumnsLoading}
                            selectedLabelColumn={fileSelectedLabelColumn}
                            selectedTextColumn={fileSelectedTextColumn}
                            selectedRelationsColumn={fileSelectedRelationsColumn}
                            onLabelColumnChange={handleFileLabelColumnChange}
                            onTextColumnChange={handleFileTextColumnChange}
                            onRelationsColumnChange={handleFileRelationsColumnChange}
                            labelError={labelLimitExceeded ? fileLabelError : (isPreviewTruncated ? '' : fileLabelError)}
                            taskType={taskType}
                            isMultilabel={isMultilabel}
                            delimiter={effectiveDelimiter}
                            setDelimiter={effectiveSetDelimiter}
                            selectedTaskType={taskType}
                        />
                    )}

                    {/* Primary section (Labels, Entities, or Summary/Response) */}
                    {fileHasLabels && isGenerativeTask(taskType) ? (
                        // Show extracted summary/response for generative task types
                        fileSelectedLabelColumn && (
                            <div style={{
                                marginTop: '8px',
                                borderBottom: 'none'
                            }}>
                                <ExtractedContentDisplay
                                    title={`Extracted ${getGenerativeOutputNoun(taskType)}`}
                                    value={extractedSummary}
                                    loading={summaryLoading}
                                    onNeedHelpClick={onOpenHelp}
                                />
                            </div>
                        )
                    ) : fileHasLabels && (taskType === "EntityRecognitionResponse" || taskType === "NERResponse")
                        ? (
                            fileSelectedLabelColumn && (
                                <div style={{
                                    marginTop: '8px',
                                    paddingTop: '8px',
                                    borderTop: '1px solid #f0f0f0',
                                    paddingBottom: '8px',
                                    borderBottom: 'none'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                        <Text variant="base-semibold" style={{ fontWeight: 600, color: '#222', fontSize: '14px', margin: 0 }}>
                                            Entities
                                        </Text>
                                        <TooltipTrigger text="Review and normalize extracted entities. Hover on the entity to see an example for that entity.">
                                            <span style={{ color: '#878787', cursor: 'help', display: 'inline-flex' }}>
                                                <SparkIcon slug="circle-help" size="small" />
                                            </span>
                                        </TooltipTrigger>
                                    </div>
                                    {labelLimitExceeded && labelLimitSamples.length > 0 ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            {labelLimitSamples.map((sample, idx) => (
                                                <Input
                                                    key={idx}
                                                    value={sample}
                                                    disabled
                                                    showLabel={false}
                                                    style={{ color: '#595959' }}
                                                />
                                            ))}
                                            <Text variant="detail-regular" style={{ color: '#8c8c8c', fontSize: '12px' }}>
                                                ...and more
                                            </Text>
                                        </div>
                                    ) : (
                                        <LabelExtractionManager
                                            labelColumn={fileSelectedLabelColumn}
                                            extractedLabels={fileExtractedLabels}
                                            loading={fileLabelsLoading}
                                            labelMappings={fileLabelMappings}
                                            onDeleteLabel={handleFileDeleteLabel}
                                            onReplaceLabel={handleFileReplaceLabel}
                                            onAddNewLabel={handleFileAddNewLabel}
                                            labelType="Entity"
                                            taskType={taskType}
                                            isMultilabel={isMultilabel}
                                            labelExamples={fileLabelExamples}
                                            availableLabels={getAvailableLabelsForType("Entity")}
                                        />
                                    )}
                                </div>
                            )
                        )
                        : (
                            (fileHasLabels && fileSelectedLabelColumn && !((taskType === TaskType.classification) && isMultilabel && !effectiveDelimiter)) && (
                                <div style={{
                                    marginTop: '8px',
                                    paddingTop: '8px',
                                    borderTop: '1px solid #f0f0f0',
                                    paddingBottom: '8px',
                                    borderBottom: 'none'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                        <Text variant="base-semibold" style={{ fontWeight: 600, color: '#222', fontSize: '14px', margin: 0 }}>
                                            Labels
                                        </Text>
                                        <TooltipTrigger text="Review and normalize extracted labels">
                                            <span style={{ color: '#878787', cursor: 'help', display: 'inline-flex' }}>
                                                <SparkIcon slug="circle-help" size="small" />
                                            </span>
                                        </TooltipTrigger>
                                    </div>
                                    {labelLimitExceeded && labelLimitSamples.length > 0 ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            {labelLimitSamples.map((sample, idx) => (
                                                <Input
                                                    key={idx}
                                                    value={sample}
                                                    disabled
                                                    showLabel={false}
                                                    style={{ color: '#595959' }}
                                                />
                                            ))}
                                            <Text variant="detail-regular" style={{ color: '#8c8c8c', fontSize: '12px' }}>
                                                ...and more
                                            </Text>
                                        </div>
                                    ) : (
                                        <LabelExtractionManager
                                            labelColumn={fileSelectedLabelColumn}
                                            extractedLabels={fileExtractedLabels}
                                            loading={fileLabelsLoading}
                                            labelMappings={fileLabelMappings}
                                            onDeleteLabel={handleFileDeleteLabel}
                                            onReplaceLabel={handleFileReplaceLabel}
                                            onAddNewLabel={handleFileAddNewLabel}
                                            labelType="Label"
                                            taskType={taskType}
                                            isMultilabel={isMultilabel}
                                            labelExamples={fileLabelExamples}
                                            availableLabels={getAvailableLabelsForType("Label")}
                                            showRejectionColumn={taskType === TaskType.classification && !isMultilabel && confidenceRejectionEnabled}
                                            rejectionLabelName={fileRejectionLabelName}
                                            onSetRejectionLabel={setFileRejectionLabelName}
                                        />
                                    )}
                                    {taskType === TaskType.classification && !isMultilabel && confidenceRejectionEnabled && (
                                        <RejectionLabelNotice
                                            extractedLabels={fileExtractedLabels}
                                            labelMappings={fileLabelMappings}
                                            rejectionLabelName={fileRejectionLabelName}
                                            onSetRejectionLabel={setFileRejectionLabelName}
                                        />
                                    )}
                                    {(taskType === TaskType.classification ||
                                        taskType === TaskType.NER ||
                                        taskType === 'EntityRecognitionResponse' ||
                                        taskType === TaskType.summarization ||
                                        taskType === TaskType.contentGeneration) && (
                                        <EmptyResponsesChoice
                                            isMultilabel={isMultilabel}
                                            taskType={taskType}
                                            value={emptyMultilabelHandling}
                                            onChange={setEmptyMultilabelHandling}
                                        />
                                    )}
                                </div>
                            )
                        )
                    }

                    {taskType === "EntityRecognitionResponse" && fileHasLabels && fileSelectedRelationsColumn && (
                        <div style={{
                            marginTop: '16px',
                            paddingTop: '12px',
                            borderTop: '1px solid #f0f0f0',
                            paddingBottom: '8px',
                            borderBottom: 'none'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                <Text variant="base-semibold" style={{ fontWeight: 600, color: '#222', fontSize: '14px', margin: 0 }}>
                                    Relations
                                </Text>
                                <TooltipTrigger text="Review and normalize extracted relation types">
                                    <span style={{ color: '#878787', cursor: 'help', display: 'inline-flex' }}>
                                        <SparkIcon slug="circle-help" size="small" />
                                    </span>
                                </TooltipTrigger>
                            </div>
                            <LabelExtractionManager
                                labelColumn={fileSelectedRelationsColumn}
                                extractedLabels={fileExtractedRelations}
                                loading={fileRelationsLoading}
                                labelMappings={fileRelationsMappings}
                                onDeleteLabel={handleFileDeleteRelation}
                                onReplaceLabel={handleFileReplaceRelation}
                                onAddNewLabel={handleFileAddNewRelation}
                                labelType="Relation"
                                taskType={taskType}
                                isMultilabel={isMultilabel}
                                loadingItemPluralLabel="relations"
                            />
                        </div>
                    )}
                </div>
            );
        } else {
            // No data source available
            return (
                <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                    No data source available for label review.
                </div>
            );
        }
    };

    const usecasesWithMissingOutputs = useMemo(() => {
        if (!multiObjectiveMode) return null;

        const ids = [
            ...Object.keys(multiObjectiveValidation.errors),
            ...Object.keys(multiObjectiveValidation.softErrors),
        ];
        const usecases = ids.map((id) => subGroup.usecases.find((u) => String(u._id) === id)).filter(Boolean);
        return usecases.map((u) => ({
            id: u._id,
            usecase_name: u.usecase_name,
            task_type: u.task_type,
            error: multiObjectiveValidation.errors[u._id],
            softError: multiObjectiveValidation.softErrors[u._id],
        }));

    }, [subGroup, multiObjectiveValidation, multiObjectiveMode]);

    const renderStep4 = () => (
        <ConfirmNoOutputsStep
            taskType={taskType}
            usecasesWithMissingOutputs={usecasesWithMissingOutputs}
        />
    );

    const renderFooter = () => {
        // Multi-objective Review Outputs: drive the primary button between
        // "Next" (advance to the next sub-task tab) and "Done" (final tab only)
        // so the user can't submit while later sub-task tabs are unreviewed.
        const usecaseIds = (subGroup?.usecases || []).map((u) => String(u._id));
        const activeIdx = usecaseIds.indexOf(String(activeUsecaseId));
        const isLastSubtask =
            !multiObjectiveMode || activeIdx === -1 || activeIdx === usecaseIds.length - 1;
        const onLabelStep = currentStep === 2;
        const advancingSubtask = onLabelStep && multiObjectiveMode && !isLastSubtask;
        const goToNextSubtask = () => {
            const nextId = usecaseIds[activeIdx + 1];
            if (nextId != null) setActiveUsecaseId(nextId);
        };

        // Single-objective single-label classification: warn when this upload
        // changes the task's already-configured "Other"/rejection label, since
        // submitting re-points (or clears) it for the whole task.
        const rejectionLabelChanged =
            onLabelStep &&
            !multiObjectiveMode &&
            taskType === TaskType.classification &&
            !isMultilabel &&
            confidenceRejectionEnabled &&
            !!existingRejectionLabelName &&
            (fileRejectionLabelName || null) !== (existingRejectionLabelName || null);

        if (currentStep === 0 && !isPreloaded) {
            return (
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '12px',
                    borderTop: '1px solid #f0f0f0',
                    paddingTop: '16px',
                    marginTop: '16px'
                }}>
                    <Button
                        variant="secondary"
                        onClick={handleSkip}
                        style={{
                            height: '32px',
                            borderRadius: '6px',
                            fontSize: '14px',
                            border: '1px solid #d9d9d9',
                            backgroundColor: '#ffffff',
                            color: '#262626',
                            padding: '0 16px',
                        }}
                    >
                        Skip
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleNext}
                        style={{
                            height: '32px',
                            borderRadius: '6px',
                            fontSize: '14px',
                            backgroundColor: '#1777ff',
                            borderColor: '#1777ff',
                            padding: '0 16px',
                        }}
                    >
                        Next
                    </Button>
                </div>
            );
        } else if (currentStep === 3 && showConfirmationStep) {
            // Confirmation step (shown at step 3 after user tries to complete without labels)
            return (
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '12px',
                    borderTop: '1px solid #f0f0f0',
                    paddingTop: '16px',
                    marginTop: '16px'
                }}>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            setShowConfirmationStep(false);
                            setCurrentStep(2);
                        }}
                        style={{
                            height: '32px',
                            borderRadius: '6px',
                            fontSize: '14px',
                            border: '1px solid #d9d9d9',
                            backgroundColor: '#ffffff',
                            color: '#262626',
                            padding: '0 16px',
                        }}
                    >
                        Back
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleConfirmUpload}
                        startIcon={uploading ? <Spinner size="small" /> : undefined}
                        disabled={uploading}
                        disableWhenReadOnly
                        style={{
                            height: '32px',
                            borderRadius: '6px',
                            fontSize: '14px',
                            backgroundColor: '#1777ff',
                            borderColor: '#1777ff',
                            padding: '0 16px',
                        }}
                    >
                        {uploading
                            ? 'Processing...'
                            : `Yes, upload without ${multiObjectiveMode
                                ? 'outputs'
                                : taskType === TaskType.classification
                                    ? 'labels'
                                    : (taskType === TaskType.NER || taskType === TaskType.NER_WITH_RELATIONS)
                                        ? 'entities'
                                        : taskType === TaskType.summarization
                                            ? 'summaries'
                                            : taskType === TaskType.contentGeneration
                                                ? 'responses'
                                                : 'labels'
                            }`}
                    </Button>
                </div>
            );
        } else {
            return (
                <div>
                    {/* Show error message above buttons for step 1 */}
                    {currentStep === 1 && step1Error && (
                        <div style={{
                            color: '#ff4d4f',
                            fontSize: '12px',
                            marginBottom: '16px',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '4px',
                            wordBreak: 'break-all',
                            overflowWrap: 'break-word',
                            maxWidth: '100%',
                            whiteSpace: 'pre-wrap'
                        }}>
                            {step1Error}
                        </div>
                    )}
                    {/* Show error message above buttons for step 3 */}
                    {currentStep === 2 && labelLimitExceeded && (
                        <div style={{
                            color: '#ff4d4f',
                            fontSize: '12px',
                            marginBottom: '16px',
                            textAlign: 'right',
                            wordBreak: 'break-all',
                            overflowWrap: 'break-word',
                            maxWidth: '100%',
                            whiteSpace: 'pre-wrap',
                            marginTop: '10px',
                        }}>
                            Please select a different column to continue.
                        </div>
                    )}
                    {currentStep === 2 && !labelLimitExceeded && step2Error && (
                        <div style={{
                            color: '#ff4d4f',
                            fontSize: '12px',
                            marginBottom: '16px',
                            textAlign: 'right',
                            wordBreak: 'break-all',
                            overflowWrap: 'break-word',
                            maxWidth: '100%',
                            whiteSpace: 'pre-wrap',
                            marginTop: '10px',
                        }}>
                            {step2Error}
                        </div>
                    )}
                    {rejectionLabelChanged && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '8px',
                            marginBottom: '16px',
                            padding: '10px 12px',
                            borderRadius: '6px',
                            backgroundColor: '#fffbe6',
                            border: '1px solid #ffe58f',
                            color: '#614700',
                            fontSize: '13px',
                            lineHeight: 1.5,
                        }}>
                            <span style={{ color: '#faad14', marginTop: '1px', display: 'inline-flex' }}>
                                <SparkIcon slug="feedback-warning-filled" size="small" />
                            </span>
                            <span>
                                {fileRejectionLabelName ? (
                                    <>
                                        This will change the task&apos;s &quot;Other&quot; label from{' '}
                                        <b>{existingRejectionLabelName}</b> to <b>{fileRejectionLabelName}</b> for
                                        the entire task.
                                    </>
                                ) : (
                                    <>
                                        This will remove the task&apos;s &quot;Other&quot; label{' '}
                                        (<b>{existingRejectionLabelName}</b>) for the entire task.
                                    </>
                                )}
                            </span>
                        </div>
                    )}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '12px',
                        borderTop: '1px solid #f0f0f0',
                        paddingTop: '16px',
                        marginTop: '16px'
                    }}>
                        <Button
                            variant="secondary"
                            onClick={handleBack}
                            style={{
                                height: '32px',
                                borderRadius: '6px',
                                fontSize: '14px',
                                border: '1px solid #d9d9d9',
                                backgroundColor: '#ffffff',
                                color: '#262626',
                                padding: '0 16px',
                            }}
                        >
                            Back
                        </Button>
                        <Button
                            variant="primary"
                            onClick={
                                onLabelStep
                                    ? (advancingSubtask ? goToNextSubtask : handleDone)
                                    : handleNext
                            }
                            startIcon={uploading ? <Spinner size="small" /> : undefined}
                            disabled={
                                uploading ||
                                (onLabelStep && labelLimitExceeded) ||
                                (onLabelStep && !advancingSubtask && multiObjectiveMode && !multiObjectiveValidation.ok)
                            }
                            disableWhenReadOnly
                            style={{
                                height: '32px',
                                borderRadius: '6px',
                                fontSize: '14px',
                                backgroundColor: (uploading || (onLabelStep && labelLimitExceeded) || (onLabelStep && !advancingSubtask && multiObjectiveMode && !multiObjectiveValidation.ok)) ? undefined : '#1777ff',
                                borderColor: (uploading || (onLabelStep && labelLimitExceeded) || (onLabelStep && !advancingSubtask && multiObjectiveMode && !multiObjectiveValidation.ok)) ? undefined : '#1777ff',
                                padding: '0 16px',
                            }}
                        >
                            {uploading ? 'Processing...' : (onLabelStep && !advancingSubtask && !showConfirmationStep) ? 'Done' : 'Next'}
                        </Button>
                    </div>
                </div>
            );
        }
    };

    return (
        <StepsModal
            visible={visible}
            onCancel={handleCancel}
            title="Data Details"
            subtitle="So we can better understand your data for better model creation."
            steps={steps}
            currentStep={currentStep - stepOffset}
            onStepChange={handleStepChange}
            width={620}
            styles={{
                content: {
                    width: '700px',
                    maxWidth: '700px',
                    margin: '0 auto',
                    padding: '8px 16px',
                }
            }}
        >
            {currentStep === 0
                ? renderStep1()
                : currentStep === 1
                    ? renderStep2()
                    : currentStep === 2
                        ? renderStep3()
                        : renderStep4()}
            {/* Truncated-preview notice should be the last element in the modal body, right above the footer */}
            {currentStep === 2 && previewTruncationNotice ? (
                <div style={{
                    width: '100%',
                    marginTop: '12px',
                    color: '#ff4d4f',
                    fontSize: '12px',
                    lineHeight: 1.4,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                }}>
                    {previewTruncationNotice}
                </div>
            ) : null}
            {renderFooter()}
        </StepsModal>
    );
};

export default DatasetUploadStepsModal;
